/**
 * Engraving Customizer v2 — Web Component
 *
 * Parallel to v1 (engraving-customizer.js). v1 stays untouched until Phase 4 cleanup.
 * Switch active customizer by swapping the snippet in sections/ei-product-gift.liquid.
 *
 * Phase 1 skeleton:
 *  ✅ Dialog open/close + lazy Fabric load
 *  ✅ Canvas init with background image, aspect-ratio-correct dimensions
 *  ✅ Boundary clamping (moving + scaling)
 *  ✅ Tab markup + auto-switch on canvas selection
 *  ✅ Save → cart upload (end-to-end, ported from v1)
 *  🟡 Library tab: parses manifest, shows count placeholder
 *  🟡 Text tab: empty stub
 *  🟡 Object tab: selection-aware placeholder
 *  🟡 Layers strip: empty container
 *  🟡 Re-edit from cart: stub
 *  🟡 Variant change: listener + console.log
 *  ❌ opentype.js Corel export: TODO comment, uses raw canvas.toSVG() for now
 */

const FABRIC_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/fabric.js/5.3.1/fabric.min.js'

/** opentype.js CDN — used only for the curves (text-to-paths) SVG export. */
const OPENTYPE_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/opentype.js/1.3.4/opentype.min.js'
/** WOFF2 decompressor (Shopify's CDN transcodes all fonts to WOFF2, which opentype.js
 *  cannot parse — we decompress to TTF in-browser before parsing). esm.sh handles the wasm. */
const WOFF2_CDN = 'https://esm.sh/wawoff2@2.0.1'

/** Singleton promise — one shared Fabric load across all component instances on the page. */
let fabricLoadedPromise = null

/** Singleton promise — one shared opentype.js load (lazy: only when a curves export runs). */
let opentypeLoadedPromise = null

/** Singleton promise — one shared WOFF2-decompressor load (lazy: only on curves export). */
let woff2LoadedPromise = null

/** Cache of parsed opentype.Font objects, keyed `"<family>::<variant>"`
 *  (variant ∈ regular|bold|italic|boldItalic). Shared across all instances so a
 *  given TTF is fetched + parsed at most once per page. Values are Promises that
 *  resolve to an `opentype.Font` (or `null` when no URL / parse failed). */
const OPENTYPE_FONT_CACHE = new Map()

/** Extra Fabric properties to include in toJSON for re-edit fidelity. */
const JSON_EXTRA_PROPS = ['_assetId', '_assetType', '_assetCategory', 'layerId', 'layerType', '_controlsVisibility']

/** Singleton tracker — one set of @font-face injections per page regardless of how
 *  many engraving-customizer-v2 instances exist. */
const FONT_FACES_INJECTED = new Set()

/** Max pixel size of the LARGER canvas side. The canvas matches the product
 *  image's aspect ratio; the longer dimension is capped here and the other side
 *  scales to preserve aspect. */
const CANVAS_MAX_PX = 900

/** SVG icon snippets for the layers strip (inline, no external dep). */
const LAYER_ICONS = {
  text: `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>`,
  svg:  `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`,
  shape:`<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>`,
}

/**
 * Built-in starter templates. Each template composes any number of library
 * frame assets + any number of text layers, all placed in normalized
 * coordinates so the layout scales to whatever canvas size the product uses.
 *
 *   frames: [{ assetId, scaleFrac, x, y }]
 *     assetId   — id of an entry in the library manifest
 *     scaleFrac — desired width as a fraction of canvas width (0..1)
 *     x, y      — center position as a fraction of canvas size (0..1)
 *
 *   layers: [{ content, family, weight, italic, sizeFrac, x, y, charSpacing? }]
 *     sizeFrac  — fontSize as a fraction of canvas height (0..1)
 *
 * Click a template tile → all frames + text spawn at once. User can then
 * edit content / move / resize like any normal object.
 */
const TEMPLATES = [
  // ── Frame-only composites (paired assets that ship together) ──────────
  {
    id: 'arrow-pair',
    name: 'Arrow Pair',
    category: 'pair',
    frames: [
      // Stacked vertically, same column. Top points right, bottom points left.
      { assetId: 'arrow-divider-right', scaleFrac: 0.75, x: 0.5, y: 0.35 },
      { assetId: 'arrow-divider-left',  scaleFrac: 0.75, x: 0.5, y: 0.65 },
    ],
    layers: [],
  },
  {
    id: 'royal-flourish-pair',
    name: 'Royal Flourish Pair',
    category: 'pair',
    frames: [
      // SVGs are named after their downward/upward flourish direction —
      // the "bottom" file curves up, so it reads as a top frame here, and vice versa.
      { assetId: 'royal-flourish-bottom', scaleFrac: 0.85, x: 0.5, y: 0.32 },
      { assetId: 'royal-flourish-top',    scaleFrac: 0.85, x: 0.5, y: 0.68 },
    ],
    layers: [],
  },

  // ── Text + frame templates ───────────────────────────────────────────
  {
    id: 'wedding-couple-classic',
    name: 'Wedding Couple',
    category: 'wedding',
    frames: [],
    layers: [
      { content: 'John & Jane',  family: 'Cormorant Garamond', weight: 700, italic: false, sizeFrac: 0.32, x: 0.5, y: 0.40 },
      { content: 'EST. 2024',    family: 'Bebas Neue',         weight: 400, italic: false, sizeFrac: 0.16, x: 0.5, y: 0.70, charSpacing: 400 },
    ],
  },
  {
    id: 'wedding-script-pair',
    name: 'Script Couple',
    category: 'wedding',
    frames: [{ assetId: 'laurel-wreath', scaleFrac: 0.85, x: 0.5, y: 0.5 }],
    layers: [
      { content: 'Olivia',  family: 'Allura', weight: 400, italic: false, sizeFrac: 0.15, x: 0.5, y: 0.30 },
      { content: '&',       family: 'Cormorant Garamond', weight: 400, italic: true, sizeFrac: 0.10, x: 0.5, y: 0.51 },
      { content: 'Ethan',   family: 'Allura', weight: 400, italic: false, sizeFrac: 0.15, x: 0.5, y: 0.72 },
    ],
  },
  {
    id: 'monogram-three-initial',
    name: 'Three-Initial Monogram',
    category: 'monogram',
    frames: [{ assetId: 'pillow-cartouche', scaleFrac: 0.92, x: 0.5, y: 0.5 }],
    layers: [
      { content: 'A B C', family: 'Cormorant Garamond', weight: 700, italic: false, sizeFrac: 0.30, x: 0.5, y: 0.5, charSpacing: 200 },
    ],
  },
  {
    id: 'anniversary',
    name: 'Anniversary',
    category: 'anniversary',
    frames: [{ assetId: 'floral-wreath', scaleFrac: 0.85, x: 0.5, y: 0.5 }],
    layers: [
      { content: '10',    family: 'Cormorant Garamond', weight: 700, italic: false, sizeFrac: 0.30, x: 0.5, y: 0.40 },
      { content: 'Years', family: 'Allura',             weight: 400, italic: false, sizeFrac: 0.13, x: 0.5, y: 0.70 },
    ],
  },
  {
    id: 'single-name-script',
    name: 'Single Name',
    category: 'name',
    frames: [],
    layers: [
      { content: 'Bennett', family: 'Lobster', weight: 400, italic: false, sizeFrac: 0.42, x: 0.5, y: 0.50 },
    ],
  },
  {
    id: 'family-est',
    name: 'Family Crest',
    category: 'family',
    frames: [{ assetId: 'pillow-cartouche', scaleFrac: 0.92, x: 0.5, y: 0.5 }],
    layers: [
      { content: 'THE HARRIS FAMILY', family: 'Bebas Neue',         weight: 400, italic: false, sizeFrac: 0.13, x: 0.5, y: 0.40, charSpacing: 200 },
      { content: 'EST. 1998',         family: 'Cormorant Garamond', weight: 400, italic: true,  sizeFrac: 0.12, x: 0.5, y: 0.62 },
    ],
  },
  {
    id: 'cheers-toast',
    name: 'Cheers',
    category: 'celebration',
    frames: [],
    layers: [
      { content: 'Cheers!',              family: 'Lobster',            weight: 400, italic: false, sizeFrac: 0.40, x: 0.5, y: 0.42 },
      { content: 'to the happy couple',  family: 'Cormorant Garamond', weight: 400, italic: true,  sizeFrac: 0.14, x: 0.5, y: 0.74 },
    ],
  },
  {
    id: 'birthday-milestone',
    name: 'Birthday',
    category: 'celebration',
    frames: [{ assetId: 'floral-wreath', scaleFrac: 0.85, x: 0.5, y: 0.5 }],
    layers: [
      { content: '50',             family: 'Bebas Neue', weight: 400, italic: false, sizeFrac: 0.24, x: 0.5, y: 0.45 },
      { content: 'Happy Birthday', family: 'Allura',     weight: 400, italic: false, sizeFrac: 0.10, x: 0.5, y: 0.61 },
    ],
  },
  {
    id: 'holiday-greeting',
    name: 'Merry & Bright',
    category: 'holiday',
    frames: [],
    layers: [
      { content: 'Merry',  family: 'Allura',             weight: 400, italic: false, sizeFrac: 0.24, x: 0.5, y: 0.30 },
      { content: '&',      family: 'Cormorant Garamond', weight: 400, italic: true,  sizeFrac: 0.13, x: 0.5, y: 0.52 },
      { content: 'Bright', family: 'Allura',             weight: 400, italic: false, sizeFrac: 0.24, x: 0.5, y: 0.74 },
    ],
  },
  {
    id: 'business-tagline',
    name: 'Business Logo',
    category: 'business',
    frames: [{ assetId: 'pillow-cartouche', scaleFrac: 0.92, x: 0.5, y: 0.5 }],
    layers: [
      { content: 'NORTHSIDE & CO', family: 'Roboto Slab',        weight: 700, italic: false, sizeFrac: 0.12, x: 0.5, y: 0.44, charSpacing: 100 },
      { content: 'est. 2019',      family: 'Cormorant Garamond', weight: 400, italic: true,  sizeFrac: 0.10, x: 0.5, y: 0.60 },
    ],
  },
  {
    id: 'wedding-date-arch',
    name: 'Wedding Date',
    category: 'wedding',
    frames: [{ assetId: 'royal-flourish-top', scaleFrac: 0.55, x: 0.5, y: 0.18 }],
    layers: [
      { content: '06.14.2024',    family: 'Cormorant Garamond', weight: 700, italic: false, sizeFrac: 0.22, x: 0.5, y: 0.55, charSpacing: 250 },
      { content: 'forever yours', family: 'Allura',             weight: 400, italic: false, sizeFrac: 0.18, x: 0.5, y: 0.80 },
    ],
  },
  {
    id: 'memorial-dates',
    name: 'In Memory',
    category: 'memorial',
    frames: [{ assetId: 'floral-wreath', scaleFrac: 0.85, x: 0.5, y: 0.5 }],
    layers: [
      { content: 'Remembered',     family: 'Cormorant Garamond', weight: 400, italic: true,  sizeFrac: 0.09, x: 0.5, y: 0.40 },
      { content: 'Robert J. Ellis',family: 'Cormorant Garamond', weight: 700, italic: false, sizeFrac: 0.115, x: 0.5, y: 0.51 },
      { content: '1942 – 2024',    family: 'Cormorant Garamond', weight: 400, italic: false, sizeFrac: 0.085, x: 0.5, y: 0.62, charSpacing: 150 },
    ],
  },
]

/**
 * Detect ligature-based monogram fonts. These fonts work by combining 2 or 3
 * input letters into one composite glyph via OpenType `liga` substitutions —
 * which means they only render correctly when the whole string is passed to
 * `fillText` in one call. Fabric IText's default char-by-char rendering breaks
 * the ligature lookup, producing missing or partial glyphs.
 */
function isMonogramFont(family) {
  if (!family) return false
  return /monogram|circle\s*monogram/i.test(family)
}

/**
 * One-time patch applied after Fabric loads:
 *
 * 1. Renders monogram-font lines via a single `fillText` call so OpenType
 *    ligatures resolve (fixes "ABC" producing 1 partial glyph instead of a
 *    composite circular monogram).
 *
 * 2. Re-measures monogram lines using `actualBoundingBoxLeft/Right` so the
 *    selection box matches the visual extent of the rendered glyph (fixes
 *    selection handles being offset to the side of the visible monogram).
 */
function patchFabricForMonograms(fabric) {
  if (!fabric || fabric._eiMonogramPatched) return
  fabric._eiMonogramPatched = true

  const fontDecl = (obj) =>
    `${obj.fontStyle || 'normal'} ${obj.fontWeight || 400} ${obj.fontSize || 24}px '${obj.fontFamily}'`

  // -- Whole-line render so ligatures fire -----------------------------
  const origRenderChars = fabric.Text.prototype._renderChars
  fabric.Text.prototype._renderChars = function(method, ctx, line, left, top, lineIndex) {
    if (isMonogramFont(this.fontFamily)) {
      ctx.save()
      ctx.font = fontDecl(this)
      ctx.textBaseline = 'alphabetic'
      const text = Array.isArray(line) ? line.join('') : String(line)
      if (method === 'fillText') {
        if (this.fill) { ctx.fillStyle = this.fill; ctx.fillText(text, left, top) }
      } else if (method === 'strokeText') {
        if (this.stroke && this.strokeWidth) {
          ctx.strokeStyle = this.stroke
          ctx.lineWidth = this.strokeWidth
          ctx.strokeText(text, left, top)
        }
      }
      ctx.restore()
      return
    }
    return origRenderChars.call(this, method, ctx, line, left, top, lineIndex)
  }

  // -- Visual-bounds measurement so selection box matches rendered glyph --
  const origMeasureLine = fabric.Text.prototype._measureLine
  fabric.Text.prototype._measureLine = function(lineIndex) {
    const baseline = origMeasureLine.call(this, lineIndex)
    if (!isMonogramFont(this.fontFamily)) return baseline
    try {
      const text = (this._textLines && this._textLines[lineIndex] || []).join('')
      const probe = fabric.util.createCanvasElement().getContext('2d')
      probe.font = fontDecl(this)
      const m = probe.measureText(text)
      const advance = m.width
      const visual = (m.actualBoundingBoxLeft || 0) + (m.actualBoundingBoxRight || 0)
      const w = Math.max(advance, visual)
      return { width: w, numOfSpaces: baseline.numOfSpaces || 0 }
    } catch (_) {
      return baseline
    }
  }
}

/* ── Helper: default object appearance (matches UX-patterns §4 / print-designer canvas.js:54-65) ── */
function applyObjectDefaults(obj) {
  obj.set({
    borderColor: '#ab2b36',    // --ei-primary
    cornerColor: '#ab2b36',
    cornerSize: 12,
    transparentCorners: false,
    globalCompositeOperation: 'source-atop',
    _controlsVisibility: { mt: false, mb: false, ml: false, mr: false }
  })
  obj.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false })
}

/* ──────────────────────────────────────────────────────────── */
/*  Web Component                                               */
/* ──────────────────────────────────────────────────────────── */

class EngravingCustomizerV2 extends HTMLElement {

  /* ── Lifecycle ─────────────────────────────────────────── */

  connectedCallback() {
    // Read all config from data attributes once
    this.config = {
      productImage:     this.dataset.productImage    || null,
      productTitle:     this.dataset.productTitle    || 'Custom Design',
      uploadVariantId:  this.dataset.uploadVariantId || '',
      maxWidthMm:       parseFloat(this.dataset.maxWidthMm)  || 80,
      maxHeightMm:      parseFloat(this.dataset.maxHeightMm) || 40,
      printable:        this.dataset.printable === 'true',
      engravingColor:   this.dataset.engravingColor  || '#000000',
      fontManifest:     [],
      existingDesignJson: this.dataset.existingDesignJson || null,
    }

    try { this.config.fontManifest = JSON.parse(this.dataset.fontManifest || '[]') } catch (e) { /* ignore */ }

    // Internal state
    this._canvasReady = false
    this.canvas = null
    this._activeTab = 'library'

    // DOM refs (live in the Liquid snippet — light DOM)
    this._dialog    = this.querySelector('[data-dialog]')
    this._canvasWrap = this.querySelector('[data-canvas-wrap]')
    this._canvasLoading = this.querySelector('[data-canvas-loading]')
    this._statusEl  = this.querySelector('[data-status]')
    this._statusText = this.querySelector('[data-status-text]')
    this._saveBtn   = this.querySelector('[data-save]')
    this._clearBtn  = this.querySelector('[data-clear]')

    // Wire trigger button(s) — both the main button and the "Edit" link in status bar
    this.querySelectorAll('[data-trigger]').forEach(btn =>
      btn.addEventListener('click', () => this.openDialog())
    )

    this.querySelector('[data-close]').addEventListener('click', () => this.closeDialog())
    this._saveBtn.addEventListener('click',  () => this.saveDesign())

    const dlSvgBtn = this.querySelector('[data-download-svg]')
    const dlSvgCurvesBtn = this.querySelector('[data-download-svg-curves]')
    const dlPngBtn = this.querySelector('[data-download-png]')
    if (dlSvgBtn) dlSvgBtn.addEventListener('click', () => this.downloadSVG())
    if (dlSvgCurvesBtn) dlSvgCurvesBtn.addEventListener('click', () => this.downloadSVGCurves())
    if (dlPngBtn) dlPngBtn.addEventListener('click', () => this.downloadMockupPNG())

    const toggleGuideBtn = this.querySelector('[data-toggle-guide]')
    if (toggleGuideBtn) toggleGuideBtn.addEventListener('click', () => this.toggleEngravingGuide())
    this._clearBtn.addEventListener('click', () => this.clearCanvas())

    // Tab clicks
    this.querySelectorAll('[data-tab]').forEach(tab =>
      tab.addEventListener('click', () => this._switchTab(tab.dataset.tab))
    )

    // Keyboard shortcuts (scoped to open dialog)
    this._onKeydown = this._handleKeydown.bind(this)
    document.addEventListener('keydown', this._onKeydown)

    // Variant change stub
    document.addEventListener('variant:change', (e) => {
      // TODO: Phase 1.11 — resize canvas to new variant bounds, clamp objects,
      //       show confirm dialog if design extends beyond new bounds
      //       (PLAN.md §1.11 + Risks #4 resolved 2026-04-24)
      console.log('[EngravingCustomizerV2] variant:change received — canvas resize not yet implemented', e.detail)
    })

    // Re-edit stub
    this._loadExistingDesign()
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeydown)
  }

  /* ── Re-edit stub ──────────────────────────────────────── */

  _loadExistingDesign() {
    // TODO: Phase 3.1 — fetch /cart.js, find existing design line item,
    //       load its _Design JSON URL into canvas on dialog open.
    if (this.config.existingDesignJson) {
      console.log('[EngravingCustomizerV2] Existing design JSON URL detected — re-edit not yet implemented:', this.config.existingDesignJson)
    }
  }

  /* ── Color logic (PLAN.md §1.7) ────────────────────────── */

  resolveColor(userPicked) {
    if (this.config.printable) return userPicked ?? '#000000'
    return this.config.engravingColor ?? '#000000'
  }

  /* ── Dialog ────────────────────────────────────────────── */

  async openDialog() {
    this._dialog.showModal()

    if (!this._canvasReady) {
      try {
        await this._loadFabric()
        this._loadFonts()
        // Brief settle — Fabric needs the dialog to be laid out before measuring
        await new Promise(r => setTimeout(r, 80))
        this._initCanvas()
        this._canvasReady = true
      } catch (err) {
        console.error('[EngravingCustomizerV2] Init failed:', err)
      }
    }
  }

  closeDialog() {
    this._dialog.close()
  }

  /* ── Lazy Fabric load (singleton promise) ──────────────── */

  _loadFabric() {
    if (fabricLoadedPromise) return fabricLoadedPromise
    fabricLoadedPromise = new Promise((resolve, reject) => {
      if (window.fabric) return resolve()
      const s = document.createElement('script')
      s.src = FABRIC_CDN
      s.onload = resolve
      s.onerror = () => {
        fabricLoadedPromise = null   // allow retry on next open
        reject(new Error('Failed to load Fabric.js'))
      }
      document.head.appendChild(s)
    })
    return fabricLoadedPromise.then(() => patchFabricForMonograms(window.fabric))
  }

  /* ── Lazy opentype.js load (singleton promise) ─────────────
   *  Loaded only when a text-to-curves SVG export is requested, mirroring the
   *  Fabric loader pattern. Resolves to the global `opentype` namespace. */
  _loadOpentype() {
    if (opentypeLoadedPromise) return opentypeLoadedPromise
    opentypeLoadedPromise = new Promise((resolve, reject) => {
      if (window.opentype) return resolve(window.opentype)
      const s = document.createElement('script')
      s.src = OPENTYPE_CDN
      s.onload = () => {
        if (window.opentype) resolve(window.opentype)
        else { opentypeLoadedPromise = null; reject(new Error('opentype.js loaded but global missing')) }
      }
      s.onerror = () => {
        opentypeLoadedPromise = null   // allow retry on next curves export
        reject(new Error('Failed to load opentype.js'))
      }
      document.head.appendChild(s)
    })
    return opentypeLoadedPromise
  }

  /** Lazy-load a WOFF2→TTF decompressor (esm.sh handles the wasm). Returns the
   *  module ({ decompress }) or null on failure. Cached as a shared promise. */
  _loadWoff2() {
    if (woff2LoadedPromise) return woff2LoadedPromise
    woff2LoadedPromise = import(WOFF2_CDN)
      .then((m) => (m && typeof m.decompress === 'function' ? m : (m && m.default) || null))
      .catch((e) => {
        woff2LoadedPromise = null   // allow retry on next export
        console.warn('[EngravingCustomizerV2] WOFF2 decompressor load failed:', e && e.message)
        return null
      })
    return woff2LoadedPromise
  }

  /**
   * Resolve the variant key (regular|bold|italic|boldItalic) a Fabric text
   * object maps to, then return the matching TTF URL from the font manifest.
   * Falls back to `regular` if the exact variant has no URL. Returns null when
   * the family is unknown or has no usable URL.
   */
  _resolveFontUrl(obj) {
    const manifest = Array.isArray(this.config.fontManifest) ? this.config.fontManifest : []
    const entry = manifest.find(f => f && f.family === obj.fontFamily)
    if (!entry || !entry.weights) return { url: null, variant: null }
    const bold   = obj.fontWeight === 'bold' || obj.fontWeight === 700 || obj.fontWeight === '700'
    const italic = obj.fontStyle === 'italic'
    const variant = bold && italic ? 'boldItalic' : bold ? 'bold' : italic ? 'italic' : 'regular'
    const url = entry.weights[variant] || entry.weights.regular || null
    return { url, variant: entry.weights[variant] ? variant : (entry.weights.regular ? 'regular' : null) }
  }

  /**
   * Load + parse (and cache) the opentype.Font for a Fabric text object.
   * Returns the parsed Font or null (no URL / load failure) — never throws.
   * Cache key is `"<family>::<variant>"`; values are shared promises so a TTF
   * is fetched + parsed at most once per page.
   */
  async _getOpentypeFont(obj) {
    const { url, variant } = this._resolveFontUrl(obj)
    if (!url) {
      console.warn('[EngravingCustomizerV2] No TTF URL for font — skipping curves conversion:', obj.fontFamily)
      return null
    }
    const key = `${obj.fontFamily}::${variant}`
    if (OPENTYPE_FONT_CACHE.has(key)) return OPENTYPE_FONT_CACHE.get(key)

    const opentype = await this._loadOpentype()
    const p = (async () => {
      try {
        // Fetch the binary ourselves (crossOrigin Shopify CDN) then parse, which
        // is more reliable than opentype.load() across CORS than callback form.
        const res = await fetch(url, { mode: 'cors' })
        if (!res.ok) throw new Error(`font fetch ${res.status}`)
        let bytes = new Uint8Array(await res.arrayBuffer())
        // Shopify's CDN transcodes fonts to WOFF2 ('wOF2'), which opentype.js can't
        // parse — decompress to TTF first.
        if (bytes[0] === 0x77 && bytes[1] === 0x4F && bytes[2] === 0x46 && bytes[3] === 0x32) {
          const w2 = await this._loadWoff2()
          if (!w2 || typeof w2.decompress !== 'function') throw new Error('woff2 decompressor unavailable')
          const out = await w2.decompress(bytes)
          bytes = out instanceof Uint8Array ? out : new Uint8Array(out)
        }
        return opentype.parse(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength))
      } catch (e) {
        console.warn('[EngravingCustomizerV2] Font parse failed for', key, e && e.message)
        return null
      }
    })()
    OPENTYPE_FONT_CACHE.set(key, p)
    return p
  }

  _loadFonts() {
    // Manifest-driven font loading: one @font-face per (family, weight) URL pair.
    // Both Monograms and Standard Google Fonts come from this.config.fontManifest
    // (uploaded by scripts/upload-google-fonts.mjs to Shopify Files). Browser
    // handles weight scaling for variable fonts via CSS axis automatically.
    const manifest = Array.isArray(this.config.fontManifest) ? this.config.fontManifest : []
    if (!manifest.length) return
    let css = ''
    const preloadList = []
    const map = {
      regular:    { weight: 400, style: 'normal' },
      bold:       { weight: 700, style: 'normal' },
      italic:     { weight: 400, style: 'italic' },
      boldItalic: { weight: 700, style: 'italic' },
    }
    for (const font of manifest) {
      if (!font.family || !font.weights) continue
      for (const [key, url] of Object.entries(font.weights)) {
        if (!url || !map[key]) continue
        const k = `${font.family}::${key}`
        if (FONT_FACES_INJECTED.has(k)) continue
        FONT_FACES_INJECTED.add(k)
        css += `@font-face { font-family: '${font.family}'; src: url('${url}') format('truetype'); ` +
               `font-weight: ${map[key].weight}; font-style: ${map[key].style}; font-display: swap; }\n`
        preloadList.push({ family: font.family, weight: map[key].weight, style: map[key].style })
      }
    }
    if (!css) return
    const style = document.createElement('style')
    style.dataset.ecFonts = ''
    style.textContent = css
    document.head.appendChild(style)

    // Pre-warm in parallel so user's first font click renders instantly.
    // Don't await — fire all at once and let them hydrate the FontFace cache.
    if (document.fonts && document.fonts.load) {
      requestAnimationFrame(() => {
        for (const f of preloadList) {
          const spec = `${f.style} ${f.weight} 24px '${f.family}'`
          document.fonts.load(spec).catch(() => {})
        }
      })
    }
  }

  /* ── Canvas init ───────────────────────────────────────── */

  _initCanvas() {
    // mm-derived dimensions: still computed here as the FALLBACK sizing (used if
    // the product image fails to load or has no dimensions). The canvas now
    // normally adopts the PRODUCT IMAGE aspect ratio instead (see _setBackground),
    // so the photo fills the frame edge-to-edge, undistorted, and the engraving is
    // placed WYSIWYG over it inside an optional engraving-area guide.
    // pixelsPerMm = 520 / maxWidthMm so that 80×40mm → 520×260px (matches v1 default).
    const ppm = 520 / this.config.maxWidthMm
    const cw  = Math.round(this.config.maxWidthMm  * ppm)   // 520 for default 80mm
    const ch  = Math.round(this.config.maxHeightMm * ppm)   // 260 for default 40mm

    // Remove any previous Fabric wrapper
    const oldContainer = this._canvasWrap.querySelector('.canvas-container')
    if (oldContainer) oldContainer.remove()

    const canvasEl = document.createElement('canvas')
    canvasEl.id = 'ec-canvas-' + Date.now()
    canvasEl.width  = cw
    canvasEl.height = ch
    this._canvasWrap.appendChild(canvasEl)

    this.canvas = new window.fabric.Canvas(canvasEl, {
      backgroundColor: '#ffffff',
      selection: true,
      preserveObjectStacking: true,
    })

    // Initial dims = mm-derived fallback; _setBackground resizes to image aspect.
    this.canvas.setDimensions({ width: cw, height: ch })

    // Apply brand color to Fabric's default selection visuals
    window.fabric.Object.prototype.borderColor = '#ab2b36'
    window.fabric.Object.prototype.cornerColor = '#ab2b36'
    window.fabric.Object.prototype.cornerSize   = 12
    window.fabric.Object.prototype.transparentCorners = false

    this._bindCanvasEvents()
    this._installStickySelection()

    // Background + final sizing is async (image load). The dimension-dependent UI
    // (mm size labels, engraving guide) is built in _finalizeCanvasReady once the
    // canvas dims are settled — for both the image-loaded and fallback paths.
    this._setBackground()
  }

  /**
   * Size the canvas to the PRODUCT IMAGE's aspect ratio and set the image as a
   * FULL-FRAME (edge-to-edge, undistorted) background. Falls back to the existing
   * mm-derived sizing if the image is missing / fails to load / has no dimensions,
   * so the customizer never ends up without a canvas.
   */
  _setBackground() {
    if (!this.canvas) return

    // No product image configured → keep mm-derived fallback dims, finalize now.
    if (!this.config.productImage) { this._finalizeCanvasReady(); return }

    window.fabric.Image.fromURL(this.config.productImage, (img) => {
      if (!this.canvas) return

      const iw = img && img.width  ? img.width  : 0
      const ih = img && img.height ? img.height : 0

      // FALLBACK: image failed to load or has no usable dimensions → keep the
      // mm-derived canvas dims already applied in _initCanvas, finalize, done.
      if (!img || !iw || !ih) {
        console.warn('[EngravingCustomizerV2] Product image unavailable — using mm-derived fallback canvas size')
        this._finalizeCanvasReady()
        return
      }

      // Canvas adopts the image aspect ratio; cap the LARGER side to CANVAS_MAX_PX.
      const aspect = iw / ih
      let cw, ch
      if (iw >= ih) { cw = CANVAS_MAX_PX;            ch = Math.round(CANVAS_MAX_PX / aspect) }
      else          { ch = CANVAS_MAX_PX;            cw = Math.round(CANVAS_MAX_PX * aspect) }

      this.canvas.setDimensions({ width: cw, height: ch })
      this.canvas.setWidth(cw)
      this.canvas.setHeight(ch)

      // FILL the canvas exactly: since canvas matches the image aspect, width &
      // height scale match → no margins, no distortion, centered.
      const scale = cw / iw
      img.set({
        scaleX: scale, scaleY: scale,
        left: cw / 2,
        top:  ch / 2,
        originX: 'center', originY: 'center',
        opacity: 1, selectable: false, evented: false,
      })
      console.log('[EngravingCustomizerV2] BG set (product-fills-frame):', { cw, ch, iw, ih, scale })
      this.canvas.setBackgroundImage(img, () => {
        this.canvas.renderAll()
        this._finalizeCanvasReady()
      })
    }, { crossOrigin: 'anonymous' })
  }

  /**
   * Runs once canvas dimensions are settled (image-loaded OR fallback path).
   * Adds the engraving-area guide, hides the loading overlay, and builds all
   * dimension-dependent UI. Idempotent-guarded so it only finalizes once.
   */
  _finalizeCanvasReady() {
    if (this._canvasFinalized) return
    this._canvasFinalized = true

    this._addEngravingGuide()

    // Hide loading overlay
    if (this._canvasLoading) this._canvasLoading.hidden = true

    this._parseManifest()
    this._buildTextUI()
    this._buildObjectUI()
    this._renderLayersStrip()

    console.log('[EngravingCustomizerV2] Canvas ready:', this.canvas.getWidth(), 'x', this.canvas.getHeight(), 'px',
      '(engraving zone px width:', this._engravingPxWidth, ')')
  }

  /**
   * Add a subtle, non-selectable dashed rectangle marking the engraving area.
   * Sized to the engraving mm ASPECT RATIO (maxWidthMm:maxHeightMm), centered,
   * fit to ~60% of canvas width (smaller if needed to keep the mm aspect within
   * the canvas height). Flagged `_eiGuide` so it is excluded from ALL exports and
   * the layers strip. Default ON; togglable via [data-toggle-guide] if present.
   * Stores its pixel width as `this._engravingPxWidth` (re-anchors mm↔px mapping).
   */
  _addEngravingGuide() {
    const cw = this.canvas.getWidth()
    const ch = this.canvas.getHeight()
    const engAspect = (this.config.maxWidthMm || 80) / (this.config.maxHeightMm || 40)

    // Default: 60% of canvas width; shrink if the resulting height exceeds 85% of canvas.
    let gw = cw * 0.6
    let gh = gw / engAspect
    const maxGh = ch * 0.85
    if (gh > maxGh) { gh = maxGh; gw = gh * engAspect }

    this._engravingPxWidth = gw
    // Centred printable zone. Design defaults (template/art scale, text size, x/y)
    // map onto THIS rect — not the full product canvas — so designs fit the
    // engraving area instead of smothering the whole product.
    this._guideRect = { x: (cw - gw) / 2, y: (ch - gh) / 2, w: gw, h: gh }

    const guide = new window.fabric.Rect({
      width: gw,
      height: gh,
      left: cw / 2,
      top:  ch / 2,
      originX: 'center', originY: 'center',
      fill: 'transparent',
      stroke: 'rgba(171, 43, 54, 0.55)',   // --ei-primary, subtle
      strokeWidth: 1,
      strokeDashArray: [6, 4],
      strokeUniform: true,
      selectable: false,
      evented: false,
      hoverCursor: 'default',
      excludeFromExport: true,   // Fabric's own toSVG/toJSON exclusion
    })
    guide._eiGuide = true        // our explicit flag, used across all exporters

    this._eiGuideObj = guide
    this.canvas.add(guide)
    // Keep the guide visually above the product bg but it does not participate in
    // selection/exports. Place at back so design objects render over it normally.
    this.canvas.sendToBack(guide)
    this.canvas.requestRenderAll()
  }

  /** Centred printable-zone rect that design defaults size/position against.
   *  Falls back to the full canvas if the guide hasn't been built yet. */
  _guide() {
    return this._guideRect || { x: 0, y: 0, w: this.canvas.getWidth(), h: this.canvas.getHeight() }
  }

  /** Toggle the engraving-area guide on/off (used if a [data-toggle-guide] exists). */
  toggleEngravingGuide() {
    if (!this.canvas || !this._eiGuideObj) return
    if (this.canvas.getObjects().includes(this._eiGuideObj)) {
      this.canvas.remove(this._eiGuideObj)
    } else {
      this.canvas.add(this._eiGuideObj)
      this.canvas.sendToBack(this._eiGuideObj)
    }
    this.canvas.requestRenderAll()
  }

  /**
   * Make the active object "sticky" — when overlapping objects exist and the
   * user clicks-and-drags over a region where multiple objects sit, prefer the
   * currently selected one instead of the topmost. This lets users select a
   * lower object via the layers strip and then move it without Fabric
   * snapping to the object on top.
   */
  _installStickySelection() {
    const canvas = this.canvas
    const origFindTarget = canvas.findTarget.bind(canvas)
    canvas.findTarget = function(e, skipGroup) {
      const active = this._activeObject
      if (active && !active.group) {
        try {
          const pointer = this.getPointer(e, true)
          if (active.containsPoint(pointer) || (active.oCoords && active._findTargetCorner && active._findTargetCorner(pointer))) {
            return active
          }
        } catch (_) { /* fall through to default */ }
      }
      return origFindTarget(e, skipGroup)
    }
  }

  /* ── Canvas events ─────────────────────────────────────── */

  _bindCanvasEvents() {
    const canvas = this.canvas

    // Auto-switch tabs on selection (UX-patterns §2 / App.vue:212-218)
    // Text objects route to Text tab; all others route to Object tab.
    canvas.on('selection:created', (e) => {
      const tab = this._tabForSelection(e.selected)
      this._switchTab(tab)
      this._updateObjectTab(e.selected)
      this._syncTextTab(e.selected)
      this._renderLayersStrip()
      this._highlightLayerCard(e.selected)
    })
    canvas.on('selection:updated', (e) => {
      const tab = this._tabForSelection(e.selected)
      this._switchTab(tab)
      this._updateObjectTab(e.selected)
      this._syncTextTab(e.selected)
      this._highlightLayerCard(e.selected)
    })
    canvas.on('before:selection:cleared', () => {
      this._switchTab('library')
    })
    canvas.on('selection:cleared', () => {
      this._updateObjectTab([])
      this._clearLayerHighlight()
    })

    // Re-render layers strip on structural changes
    canvas.on('object:added',    () => this._renderLayersStrip())
    canvas.on('object:removed',  () => this._renderLayersStrip())
    canvas.on('object:modified', () => this._renderLayersStrip())

    // Boundary clamping — moving (UX-patterns §4 / PLAN.md §1.6)
    canvas.on('object:moving', ({ target: obj }) => {
      obj.setCoords()
      const b = obj.getBoundingRect(true)
      if (b.left < 0)                              obj.left -= b.left
      if (b.top  < 0)                              obj.top  -= b.top
      if (b.left + b.width  > canvas.width)  obj.left -= (b.left + b.width  - canvas.width)
      if (b.top  + b.height > canvas.height) obj.top  -= (b.top  + b.height - canvas.height)
    })

    // Boundary clamping — scaling (cap scale so object can't exceed canvas)
    canvas.on('object:scaling', ({ target: obj }) => {
      obj.setCoords()
      const b = obj.getBoundingRect(true)
      // Clamp position if the bounding rect has already drifted outside
      if (b.left < 0)                              obj.left -= b.left
      if (b.top  < 0)                              obj.top  -= b.top
      if (b.left + b.width  > canvas.width)  obj.left -= (b.left + b.width  - canvas.width)
      if (b.top  + b.height > canvas.height) obj.top  -= (b.top  + b.height - canvas.height)
      // If the object would be wider/taller than the canvas, cap its scale
      if (obj.getScaledWidth()  > canvas.width)
        obj.scaleX = (canvas.width  / obj.width)  * (obj.scaleX > 0 ? 1 : -1)
      if (obj.getScaledHeight() > canvas.height)
        obj.scaleY = (canvas.height / obj.height) * (obj.scaleY > 0 ? 1 : -1)
    })

    // Rotating — stub; real clamping would need bounding-rect math post-rotate
    canvas.on('object:rotating', ({ target: obj }) => {
      // TODO: clamp object back inside canvas after rotate completes
      obj.setCoords()
    })

    // Mouse-wheel zoom (ported from v1, lines 167-175)
    canvas.on('mouse:wheel', (opt) => {
      const delta = opt.e.deltaY
      let zoom = canvas.getZoom()
      zoom *= 0.999 ** delta
      zoom = Math.min(Math.max(zoom, 0.5), 3)
      canvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom)
      opt.e.preventDefault()
      opt.e.stopPropagation()
    })

    // ── Drag-to-pan the canvas view ──────────────────────────
    // Drag EMPTY canvas area to move the view around (at any zoom). Dragging an
    // object still moves the object; a plain click on empty space still
    // deselects; double-click on empty space resets zoom + position.
    // Rubber-band group-select is disabled (engraving is single-object focused)
    // so an empty-area drag always pans instead of drawing a selection box.
    canvas.selection = false

    let isPanning = false
    let panLastX = 0
    let panLastY = 0
    let panScale = 1   // CSS-pixel → canvas-pixel ratio (the canvas is usually CSS-scaled)

    canvas.on('mouse:down', (opt) => {
      if (opt.target) return                 // on an object → let move/select work
      isPanning = true
      const rect = canvas.upperCanvasEl.getBoundingClientRect()
      panScale = rect.width ? canvas.getWidth() / rect.width : 1
      const p = opt.e.touches ? opt.e.touches[0] : opt.e
      panLastX = p.clientX
      panLastY = p.clientY
      canvas.setCursor('grabbing')
    })

    canvas.on('mouse:move', (opt) => {
      if (isPanning) {
        const p = opt.e.touches ? opt.e.touches[0] : opt.e
        canvas.relativePan(new window.fabric.Point(
          (p.clientX - panLastX) * panScale,
          (p.clientY - panLastY) * panScale
        ))
        panLastX = p.clientX
        panLastY = p.clientY
        if (opt.e.preventDefault) opt.e.preventDefault()
        return
      }
      // Grab affordance whenever the pointer is over empty canvas
      canvas.defaultCursor = opt.target ? 'default' : 'grab'
    })

    canvas.on('mouse:up', () => {
      if (!isPanning) return
      isPanning = false
      canvas.setCursor('grab')
    })

    // Double-click empty space → reset zoom + pan (escape hatch when lost)
    canvas.on('mouse:dblclick', (opt) => {
      if (opt.target) return
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0])
      canvas.requestRenderAll()
    })
  }

  /* ── Tab management ────────────────────────────────────── */

  /** Returns which tab to show based on the selected objects. */
  _tabForSelection(selected) {
    if (!selected || selected.length === 0) return 'library'
    if (selected.length === 1) {
      const obj = selected[0]
      if (obj.type === 'i-text' || obj.type === 'text' || obj.type === 'textbox') return 'text'
    }
    return 'object'
  }

  _switchTab(name) {
    this._activeTab = name
    this.querySelectorAll('[data-tab]').forEach(btn => {
      const active = btn.dataset.tab === name
      btn.classList.toggle('is-active', active)
      btn.setAttribute('aria-selected', active ? 'true' : 'false')
    })
    this.querySelectorAll('[data-tab-pane]').forEach(pane => {
      const active = pane.dataset.tabPane === name
      pane.classList.toggle('is-active', active)
      pane.hidden = !active
    })
  }

  /* ── Library tab ──────────────────────────────────────── */

  _parseManifest() {
    try {
      const scriptEl = this.querySelector('script[data-library-manifest]')
      if (!scriptEl || !scriptEl.textContent.trim()) {
        this._manifest = { assets: [], categories: [] }
      } else {
        const raw = JSON.parse(scriptEl.textContent)
        this._manifest = {
          assets:     Array.isArray(raw.assets)     ? raw.assets     : [],
          categories: Array.isArray(raw.categories) ? raw.categories : [],
        }
      }
    } catch (e) {
      console.warn('[EngravingCustomizerV2] Manifest parse error:', e)
      this._manifest = { assets: [], categories: [] }
    }
    this._buildLibraryUI()
  }

  _buildLibraryUI() {
    const pane = this.querySelector('[data-tab-pane="library"]')
    if (!pane) return

    const { assets, categories } = this._manifest
    this._libraryFilter = 'all'
    this._librarySearch = ''

    pane.innerHTML = ''

    // ── Upload-your-own row
    const uploadWrap = document.createElement('div')
    uploadWrap.className = 'ec-lib-upload-wrap'
    uploadWrap.innerHTML = `
      <label class="ec-lib-upload">
        <input type="file" accept=".svg,image/svg+xml" data-svg-upload hidden>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
        <span>Upload SVG</span>
      </label>
    `
    uploadWrap.querySelector('[data-svg-upload]').addEventListener('change', (e) => {
      const f = e.target.files?.[0]
      if (f) this._handleUploadedSvg(f)
      e.target.value = '' // allow re-uploading the same filename
    })
    pane.appendChild(uploadWrap)

    // ── Search row
    const searchWrap = document.createElement('div')
    searchWrap.className = 'ec-lib-search-wrap'
    const searchInput = document.createElement('input')
    searchInput.type = 'search'
    searchInput.className = 'ec-lib-search'
    searchInput.placeholder = 'Search…'
    searchInput.setAttribute('aria-label', 'Search library')
    searchInput.addEventListener('input', (e) => {
      this._librarySearch = e.target.value.toLowerCase()
      this._renderLibraryGrid()
    })
    searchWrap.appendChild(searchInput)
    pane.appendChild(searchWrap)

    // ── Category pills (Templates first, then Shapes, then asset categories)
    const pillsWrap = document.createElement('div')
    pillsWrap.className = 'ec-lib-pills'
    const allPills = ['templates', 'shapes', 'all', ...categories]
    allPills.forEach(cat => {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'ec-lib-pill' + (cat === 'templates' ? ' is-active' : '')
      const labelMap = { templates: 'Templates', shapes: 'Shapes', all: 'All' }
      btn.textContent = labelMap[cat] || (cat.charAt(0).toUpperCase() + cat.slice(1))
      btn.dataset.cat = cat
      btn.addEventListener('click', () => {
        this._libraryFilter = cat
        pillsWrap.querySelectorAll('.ec-lib-pill').forEach(p =>
          p.classList.toggle('is-active', p.dataset.cat === cat)
        )
        this._renderLibraryGrid()
      })
      pillsWrap.appendChild(btn)
    })
    pane.appendChild(pillsWrap)
    this._libraryFilter = 'templates'

    // ── Grid container
    const grid = document.createElement('div')
    grid.className = 'ec-lib-grid'
    grid.dataset.libGrid = ''
    pane.appendChild(grid)

    if (assets.length === 0) {
      grid.innerHTML = '<p class="ec-tab-pane__placeholder">No library assets configured yet.</p>'
    } else {
      this._renderLibraryGrid()
    }
  }

  _renderLibraryGrid() {
    const grid = this.querySelector('[data-lib-grid]')
    if (!grid || !this._manifest) return

    const filter = this._libraryFilter || 'templates'
    const search = this._librarySearch || ''

    grid.innerHTML = ''

    if (filter === 'templates') {
      this._renderTemplateTiles(grid, search)
      return
    }
    if (filter === 'shapes') {
      this._renderShapeTiles(grid, search)
      return
    }

    // Library asset tiles. Hide individual entries that are now exposed
    // only as part of a paired Template (so the user picks the pair).
    const HIDDEN_ASSET_IDS = new Set([
      'arrow-divider-left', 'arrow-divider-right',
      'royal-flourish-top', 'royal-flourish-bottom',
    ])

    const { assets } = this._manifest
    const filtered = assets.filter(asset => {
      if (HIDDEN_ASSET_IDS.has(asset.id)) return false
      if (filter !== 'all' && asset.category !== filter) return false
      if (search) {
        const haystack = ((asset.name || '') + ' ' + (asset.tags || []).join(' ')).toLowerCase()
        if (!haystack.includes(search)) return false
      }
      return true
    })

    if (filtered.length === 0) {
      grid.innerHTML = '<p class="ec-tab-pane__placeholder">No matching assets.</p>'
      return
    }

    filtered.forEach(asset => {
      const tile = document.createElement('button')
      tile.type = 'button'
      tile.className = 'ec-lib-tile'
      tile.title = asset.name || asset.id
      tile.innerHTML = `
        <img src="${asset.thumbUrl}" loading="lazy" alt="${asset.name || asset.id}" class="ec-lib-tile__img">
        <span class="ec-lib-tile__name">${asset.name || asset.id}</span>
      `
      tile.addEventListener('click', () => this.addLibraryAsset(asset))
      grid.appendChild(tile)
    })
  }

  /**
   * Render the Templates pill grid. Tiles include:
   *  • Curated TEMPLATES (frame-only pairs and frame+text composites)
   *  • Every individual library frame, exposed as a one-click "blank" frame
   *    template (no text), so users see all available frames in one place.
   *
   * Each tile uses an inline SVG preview that mirrors the actual layout
   * (frame thumbs positioned per-template + text in the template's font),
   * so it's a faithful preview of what dropping the template will produce.
   */
  _renderTemplateTiles(grid, search) {
    const HIDDEN_ASSET_IDS = new Set([
      'arrow-divider-left', 'arrow-divider-right',
      'royal-flourish-top', 'royal-flourish-bottom',
    ])

    const assetById = new Map(this._manifest.assets.map(a => [a.id, a]))

    // Synthesize "frame-only" templates from each visible library asset.
    const frameTemplates = this._manifest.assets
      .filter(a => !HIDDEN_ASSET_IDS.has(a.id))
      .map(a => ({
        id: `frame-${a.id}`,
        name: a.name || a.id,
        category: 'frame',
        frames: [{ assetId: a.id, scaleFrac: 0.7, x: 0.5, y: 0.5 }],
        layers: [],
        _isBareFrame: true,
      }))

    const all = [...TEMPLATES, ...frameTemplates]
    const filtered = all.filter(t => {
      if (!search) return true
      const haystack = (t.name + ' ' + (t.category || '')).toLowerCase()
      return haystack.includes(search)
    })

    if (filtered.length === 0) {
      grid.innerHTML = '<p class="ec-tab-pane__placeholder">No matching templates.</p>'
      return
    }

    filtered.forEach(tmpl => {
      const tile = document.createElement('button')
      tile.type = 'button'
      tile.className = 'ec-lib-tile ec-lib-tile--tmpl'
      tile.title = tmpl.name
      tile.innerHTML =
        this._buildTemplatePreviewSVG(tmpl, assetById) +
        `<span class="ec-lib-tile__name">${this._escapeHTML(tmpl.name)}</span>`
      tile.addEventListener('click', () => this.addTemplate(tmpl))
      grid.appendChild(tile)
    })
  }

  /**
   * Builds an inline SVG preview that mirrors the canvas layout: frames placed
   * at their normalized (x, y) and scaled to scaleFrac of the canvas width;
   * text layers rendered in the template's actual font + size at their
   * normalized position. The viewBox is 2:1 (matches the default 80×40mm
   * engraving aspect) and the tile letterboxes via preserveAspectRatio.
   */
  _buildTemplatePreviewSVG(tmpl, assetById) {
    const VW = 200, VH = 100   // viewBox units (2:1 like default canvas)
    let inner = ''

    // Frames (drawn first so text overlays them)
    for (const f of (tmpl.frames || [])) {
      const a = assetById.get(f.assetId)
      if (!a) continue
      const w = VW * (f.scaleFrac || 0.5)
      const h = w * 0.5    // approximate; tile thumbs are square but content is centered
      const x = (VW * (f.x ?? 0.5)) - (w / 2)
      const y = (VH * (f.y ?? 0.5)) - (h / 2)
      inner += `<image href="${a.thumbUrl}" x="${x}" y="${y}" width="${w}" height="${h}" preserveAspectRatio="xMidYMid meet"/>`
    }

    // Text layers
    for (const layer of (tmpl.layers || [])) {
      const fontSize = Math.max(4, VH * (layer.sizeFrac || 0.2))
      const x = VW * (layer.x ?? 0.5)
      const y = VH * (layer.y ?? 0.5)
      const family = (layer.family || 'Cormorant Garamond').replace(/'/g, '&apos;')
      inner += `<text x="${x}" y="${y}" text-anchor="middle" dominant-baseline="middle" ` +
               `font-size="${fontSize}" font-family="'${family}'" ` +
               `font-weight="${layer.weight || 400}" font-style="${layer.italic ? 'italic' : 'normal'}" ` +
               `fill="#1a1c1c">${this._escapeHTML(layer.content)}</text>`
    }

    return `<div class="ec-tmpl-preview"><svg viewBox="0 0 ${VW} ${VH}" preserveAspectRatio="xMidYMid meet" class="ec-tmpl-preview__svg" xmlns="http://www.w3.org/2000/svg">${inner}</svg></div>`
  }

  _escapeHTML(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]))
  }

  async addLibraryAsset(asset, opts = {}) {
    if (!this.canvas) return
    const cw = this.canvas.getWidth()
    const ch = this.canvas.getHeight()
    try {
      const svgText = await fetch(asset.svgUrl).then(r => r.text())
      return await new Promise((resolve) => {
        window.fabric.loadSVGFromString(svgText, (objects, options) => {
          if (!objects || objects.length === 0) {
            console.warn('[EngravingCustomizerV2] SVG loaded no objects:', asset.svgUrl)
            resolve(null)
            return
          }
          const group = window.fabric.util.groupSVGElements(objects, options)

          // Position + scale relative to the printable zone (not the whole product).
          const g = this._guide()
          const scaleFrac = typeof opts.scaleFrac === 'number' ? opts.scaleFrac : 0.5
          let baseScale = (g.w * scaleFrac) / Math.max(group.width || 1, group.height || 1)
          // Never let the art exceed the engraving zone in either dimension.
          const fitScale = Math.min(g.w / (group.width || 1), g.h / (group.height || 1))
          if (baseScale > fitScale) baseScale = fitScale
          group.scale(baseScale)

          const xFrac = typeof opts.x === 'number' ? opts.x : 0.5
          const yFrac = typeof opts.y === 'number' ? opts.y : 0.5

          group.set({
            left: g.x + g.w * xFrac,
            top:  g.y + g.h * yFrac,
            originX: 'center',
            originY: 'center',
            _assetId:       asset.id,
            _assetType:     'frame',
            _assetCategory: asset.category || '',
            layerId:   Date.now() + Math.floor(Math.random() * 1000),
            layerType: 'svg',
          })
          applyObjectDefaults(group)
          if (!this.config.printable) {
            this._recolorGroup(group, this.resolveColor())
          }
          this.canvas.add(group)
          if (!opts.suppressActivate) {
            this.canvas.setActiveObject(group)
          }
          this.canvas.requestRenderAll()
          resolve(group)
        })
      })
    } catch (err) {
      console.error('[EngravingCustomizerV2] Failed to load SVG asset:', asset.id, err)
      return null
    }
  }

  /**
   * Compose a starter template — one optional set of frames + 0..N text
   * layers — and add everything in a single click.
   */
  async addTemplate(tmpl) {
    if (!this.canvas) return
    const cw = this.canvas.getWidth()
    const ch = this.canvas.getHeight()
    const assetById = new Map(this._manifest.assets.map(a => [a.id, a]))

    // Frames first (so text layers stack on top)
    for (const f of (tmpl.frames || [])) {
      const asset = assetById.get(f.assetId)
      if (!asset) {
        console.warn('[EngravingCustomizerV2] Template references missing asset:', f.assetId)
        continue
      }
      await this.addLibraryAsset(asset, {
        scaleFrac: f.scaleFrac, x: f.x, y: f.y, suppressActivate: true,
      })
    }

    // Text layers — sized/positioned within the printable zone.
    const g = this._guide()
    const fill = this.config.printable ? '#000000' : this.resolveColor()
    let lastObj = null
    for (const layer of (tmpl.layers || [])) {
      const fontSize = Math.max(8, Math.round(g.h * (layer.sizeFrac || 0.2)))
      const text = new window.fabric.IText(layer.content, {
        left: g.x + g.w * (layer.x ?? 0.5),
        top:  g.y + g.h * (layer.y ?? 0.5),
        originX: 'center',
        originY: 'center',
        fontFamily:  layer.family || 'Cormorant Garamond',
        fontWeight:  layer.weight || 400,
        fontStyle:   layer.italic ? 'italic' : 'normal',
        fontSize,
        fill,
        charSpacing: layer.charSpacing || 0,
        layerId:   Date.now() + Math.floor(Math.random() * 1000),
        layerType: 'text',
      })
      applyObjectDefaults(text)
      this.canvas.add(text)
      lastObj = text

      // Preload font so first render shows the right typeface
      if (document.fonts && document.fonts.load) {
        const spec = `${layer.italic ? 'italic' : 'normal'} ${layer.weight || 400} ${fontSize}px '${layer.family}'`
        document.fonts.load(spec).then(() => {
          text.dirty = true
          this.canvas.requestRenderAll()
        }).catch(() => {})
      }
    }

    if (lastObj) this.canvas.setActiveObject(lastObj)
    this.canvas.requestRenderAll()
  }

  /* ── Shapes (build-your-own primitives) ────────────────── */

  _renderShapeTiles(grid, search) {
    const SHAPES = [
      { id: 'rect',      name: 'Rectangle' },
      { id: 'rounded',   name: 'Rounded Rect' },
      { id: 'circle',    name: 'Circle' },
      { id: 'ellipse',   name: 'Ellipse' },
      { id: 'triangle',  name: 'Triangle' },
      { id: 'line',      name: 'Line' },
      { id: 'star',      name: 'Star' },
    ]
    const filtered = search ? SHAPES.filter(s => s.name.toLowerCase().includes(search)) : SHAPES
    if (!filtered.length) {
      grid.innerHTML = '<p class="ec-tab-pane__placeholder">No matching shapes.</p>'
      return
    }
    filtered.forEach(s => {
      const tile = document.createElement('button')
      tile.type = 'button'
      tile.className = 'ec-lib-tile ec-lib-tile--shape'
      tile.title = s.name
      tile.innerHTML = `${this._buildShapePreviewSVG(s.id)}<span class="ec-lib-tile__name">${s.name}</span>`
      tile.addEventListener('click', () => this.addShape(s.id))
      grid.appendChild(tile)
    })
  }

  _buildShapePreviewSVG(type) {
    const svgWrap = (inner) =>
      `<div class="ec-tmpl-preview"><svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" class="ec-tmpl-preview__svg" xmlns="http://www.w3.org/2000/svg" stroke="#1a1c1c" stroke-width="3" fill="none">${inner}</svg></div>`
    switch (type) {
      case 'rect':     return svgWrap('<rect x="20" y="30" width="60" height="40"/>')
      case 'rounded':  return svgWrap('<rect x="20" y="30" width="60" height="40" rx="10" ry="10"/>')
      case 'circle':   return svgWrap('<circle cx="50" cy="50" r="28"/>')
      case 'ellipse':  return svgWrap('<ellipse cx="50" cy="50" rx="35" ry="22"/>')
      case 'triangle': return svgWrap('<polygon points="50,18 82,80 18,80"/>')
      case 'line':     return svgWrap('<line x1="15" y1="50" x2="85" y2="50"/>')
      case 'star':     return svgWrap('<polygon points="50,12 60,40 90,40 66,58 75,86 50,68 25,86 34,58 10,40 40,40"/>')
      default:         return svgWrap('')
    }
  }

  /**
   * Drop a generic shape primitive onto the canvas. Stroke uses the engraving
   * color (or black for printable products); fill is none by default so the
   * shape reads as an outline. User can flip to fill via the Object tab.
   */
  addShape(type) {
    if (!this.canvas) return
    const g = this._guide()
    const baseSize = Math.min(g.w, g.h) * 0.7
    const stroke = this.config.printable ? '#000000' : this.resolveColor()
    const common = {
      left: g.x + g.w / 2,
      top:  g.y + g.h / 2,
      originX: 'center',
      originY: 'center',
      fill: 'transparent',
      stroke,
      strokeWidth: 3,
      strokeUniform: true,
      layerId:   Date.now() + Math.floor(Math.random() * 1000),
      layerType: 'shape',
      _shapeType: type,
    }

    let obj
    const f = window.fabric
    switch (type) {
      case 'rect':
        obj = new f.Rect({ ...common, width: baseSize, height: baseSize * 0.6 })
        break
      case 'rounded':
        obj = new f.Rect({ ...common, width: baseSize, height: baseSize * 0.6, rx: 12, ry: 12 })
        break
      case 'circle':
        obj = new f.Circle({ ...common, radius: baseSize / 2 })
        break
      case 'ellipse':
        obj = new f.Ellipse({ ...common, rx: baseSize / 2, ry: baseSize / 3 })
        break
      case 'triangle':
        obj = new f.Triangle({ ...common, width: baseSize, height: baseSize * 0.9 })
        break
      case 'line':
        obj = new f.Line(
          [-baseSize / 2, 0, baseSize / 2, 0],
          { ...common, fill: undefined }
        )
        break
      case 'star': {
        const r1 = baseSize / 2, r2 = r1 * 0.45, n = 5
        const pts = []
        for (let i = 0; i < n * 2; i++) {
          const a = (Math.PI / n) * i - Math.PI / 2
          const r = i % 2 === 0 ? r1 : r2
          pts.push({ x: Math.cos(a) * r, y: Math.sin(a) * r })
        }
        obj = new f.Polygon(pts, { ...common })
        break
      }
      default: return
    }

    applyObjectDefaults(obj)
    this.canvas.add(obj)
    this.canvas.setActiveObject(obj)
    this.canvas.requestRenderAll()
  }

  /* ── User SVG upload ────────────────────────────────────── */

  async _handleUploadedSvg(file) {
    if (!this.canvas) return
    if (file.size > 2 * 1024 * 1024) {
      alert('SVG file is too large (max 2 MB).')
      return
    }
    if (!/svg/i.test(file.type) && !file.name.toLowerCase().endsWith('.svg')) {
      alert('Please upload an SVG file.')
      return
    }
    try {
      const svgText = await file.text()
      // Reject SVGs that embed scripts or external resources
      if (/<script\b/i.test(svgText) || /xlink:href\s*=\s*["']https?:/i.test(svgText)) {
        alert('SVG contains scripts or remote references — not allowed.')
        return
      }
      window.fabric.loadSVGFromString(svgText, (objects, options) => {
        if (!objects || !objects.length) {
          alert('Could not parse SVG.')
          return
        }
        const group = window.fabric.util.groupSVGElements(objects, options)
        const g = this._guide()
        const longEdge = Math.max(group.width || 1, group.height || 1)
        let scale = (g.w * 0.6) / longEdge
        const fit = Math.min(g.w / (group.width || 1), g.h / (group.height || 1))
        if (scale > fit) scale = fit
        group.scale(scale)
        group.set({
          left: g.x + g.w / 2,
          top:  g.y + g.h / 2,
          originX: 'center',
          originY: 'center',
          _assetType: 'user-upload',
          _userFilename: file.name,
          layerId:   Date.now() + Math.floor(Math.random() * 1000),
          layerType: 'svg',
        })
        applyObjectDefaults(group)
        if (!this.config.printable) this._recolorGroup(group, this.resolveColor())
        this.canvas.add(group)
        this.canvas.setActiveObject(group)
        this.canvas.requestRenderAll()
      })
    } catch (err) {
      console.error('[EngravingCustomizerV2] SVG upload failed:', err)
      alert('Failed to load SVG: ' + err.message)
    }
  }

  /**
   * Walk a Fabric group's children and override fill+stroke on every path.
   * Handles nested groups (ungroup-then-recurse).
   */
  _recolorGroup(groupOrObj, color) {
    const children = (typeof groupOrObj.getObjects === 'function')
      ? groupOrObj.getObjects()
      : (groupOrObj._objects || [])

    if (children.length > 0) {
      children.forEach(child => this._recolorGroup(child, color))
    } else {
      // Leaf path/shape — set color where the existing value is meaningful
      const hasFill   = groupOrObj.fill   && groupOrObj.fill   !== '' && groupOrObj.fill   !== 'none'
      const hasStroke = groupOrObj.stroke && groupOrObj.stroke !== '' && groupOrObj.stroke !== 'none'
      if (hasFill)   groupOrObj.set('fill',   color)
      if (hasStroke) groupOrObj.set('stroke', color)
      // If both are absent, set fill so the shape is visible
      if (!hasFill && !hasStroke) groupOrObj.set('fill', color)
    }
  }

  /* ── Text tab ──────────────────────────────────────────── */

  _buildTextUI() {
    const pane = this.querySelector('[data-tab-pane="text"]')
    if (!pane || pane.dataset.built) return
    pane.dataset.built = '1'

    // mm↔px mapping is anchored to the ENGRAVING ZONE width (the printable guide),
    // not the whole product photo, so the mm size labels stay meaningful. Falls
    // back to canvas width if the guide width isn't available yet.
    const pixelsPerMm = (this._engravingPxWidth || this.canvas.width) / this.config.maxWidthMm

    pane.innerHTML = `
      <div class="ec-ctrl-group">
        <label class="ec-label" for="ec-text-content">Text</label>
        <textarea id="ec-text-content" class="ec-text-content ec-textarea" rows="3" placeholder="Enter your text…"></textarea>
      </div>

      <div class="ec-ctrl-group">
        <label class="ec-label" for="ec-font-select">Font</label>
        <select id="ec-font-select" class="ec-select"></select>
        <p class="ec-font-hint" data-font-hint hidden></p>
      </div>

      <div class="ec-ctrl-group">
        <label class="ec-label">
          Size
          <span class="ec-label-value" data-size-label>24px · ${(24 / pixelsPerMm).toFixed(1)}mm</span>
        </label>
        <input type="range" id="ec-font-size" class="ec-range" min="8" max="120" step="1" value="24">
      </div>

      <div class="ec-ctrl-group">
        <label class="ec-label">Style</label>
        <div class="ec-toggle-group">
          <button type="button" class="ec-toggle-btn" data-style-bold title="Bold"><b>B</b></button>
          <button type="button" class="ec-toggle-btn" data-style-italic title="Italic"><i>I</i></button>
          <button type="button" class="ec-toggle-btn" data-style-underline title="Underline"><u>U</u></button>
        </div>
      </div>

      <div class="ec-ctrl-group" data-color-row>
        <label class="ec-label" for="ec-text-color">Color</label>
        <div class="ec-color-row">
          <input type="color" id="ec-text-color" class="ec-color-input" value="#000000">
          <span class="ec-color-hex" data-color-hex>#000000</span>
        </div>
      </div>

      <div class="ec-ctrl-group">
        <label class="ec-label">
          <input type="checkbox" data-outline-toggle class="ec-checkbox"> Outline
        </label>
        <div class="ec-outline-sub" data-outline-sub hidden>
          <div class="ec-color-row" style="margin-bottom:6px">
            <input type="color" data-outline-color class="ec-color-input" value="#000000">
            <span class="ec-label" style="margin:0">Color</span>
          </div>
          <label class="ec-label">
            Width
            <span class="ec-label-value" data-outline-width-label>2px</span>
          </label>
          <input type="range" data-outline-width class="ec-range" min="0" max="10" step="1" value="2">
        </div>
      </div>

      <button type="button" class="ec-btn ec-btn--primary ec-btn--full" data-add-text>Add Text</button>
    `

    // Hide color row for non-printable products
    if (!this.config.printable) {
      const colorRow = pane.querySelector('[data-color-row]')
      if (colorRow) colorRow.hidden = true
    }

    // Populate font select
    this._buildFontSelect(pane.querySelector('#ec-font-select'))

    // Wire controls
    const textarea      = pane.querySelector('.ec-text-content')
    const fontSelect    = pane.querySelector('#ec-font-select')
    const sizeRange     = pane.querySelector('#ec-font-size')
    const sizeLabel     = pane.querySelector('[data-size-label]')
    const boldBtn       = pane.querySelector('[data-style-bold]')
    const italicBtn     = pane.querySelector('[data-style-italic]')
    const underlineBtn  = pane.querySelector('[data-style-underline]')
    const colorInput    = pane.querySelector('#ec-text-color')
    const colorHex      = pane.querySelector('[data-color-hex]')
    const outlineToggle = pane.querySelector('[data-outline-toggle]')
    const outlineSub    = pane.querySelector('[data-outline-sub]')
    const outlineColor  = pane.querySelector('[data-outline-color]')
    const outlineWidth  = pane.querySelector('[data-outline-width]')
    const outlineLabel  = pane.querySelector('[data-outline-width-label]')
    const addBtn        = pane.querySelector('[data-add-text]')

    // Live text update
    textarea.addEventListener('input', () => {
      const obj = this._getActiveText()
      if (obj) {
        obj.set('text', textarea.value)
        obj.dirty = true
        this.canvas.requestRenderAll()
        this._renderLayersStrip()
      }
    })

    // Font — waits for the actual TTF to fetch+parse before re-rendering, otherwise
    // Fabric caches a system-font fallback bitmap and the new font never appears
    // until something else triggers a re-render.
    const fontHint = pane.querySelector('[data-font-hint]')
    const monogramSlots = (family) => {
      const f = (family || '').toLowerCase()
      if (/\bthree\b/.test(f)) return 3
      if (/\btwo\b/.test(f))   return 2
      return 0
    }
    const updateFontHint = (family) => {
      if (!fontHint) return
      const slots = monogramSlots(family)
      let msg = ''
      if (slots === 3) msg = 'Tip: enter exactly 3 letters. Note: only some 3-letter combinations have designed glyphs in this font.'
      else if (slots === 2) msg = 'Tip: enter exactly 2 letters — they render as paired halves of a circular monogram.'
      fontHint.textContent = msg
      fontHint.hidden = !msg

      // Cap the textarea length so users can't add extra half-circles.
      if (textarea) {
        if (slots > 0) {
          textarea.maxLength = slots
          if (textarea.value.length > slots) {
            textarea.value = textarea.value.slice(0, slots)
            textarea.dispatchEvent(new Event('input', { bubbles: true }))
          }
        } else {
          textarea.removeAttribute('maxlength')
        }
      }
    }

    fontSelect.addEventListener('change', async () => {
      updateFontHint(fontSelect.value)
      const obj = this._getActiveText()
      if (!obj) return
      const family = fontSelect.value
      // Probe at the object's actual size and weight so the right variant is loaded
      const px = obj.fontSize || 24
      const weight = obj.fontWeight === 'bold' ? '700' : '400'
      const style = obj.fontStyle === 'italic' ? 'italic' : 'normal'
      try {
        await document.fonts.load(`${style} ${weight} ${px}px '${family}'`)
      } catch (e) { /* ignore — fall through to render anyway */ }
      obj.set('fontFamily', family)
      obj.dirty = true
      this.canvas.requestRenderAll()
    })

    // Show hint immediately if a monogram font is the initial value
    updateFontHint(fontSelect.value)

    // Size
    sizeRange.addEventListener('input', () => {
      const px = parseInt(sizeRange.value, 10)
      sizeLabel.textContent = `${px}px · ${(px / pixelsPerMm).toFixed(1)}mm`
      const obj = this._getActiveText()
      if (obj) {
        obj.set('fontSize', px)
        obj.dirty = true
        this.canvas.requestRenderAll()
      }
    })

    // Style toggles — wait for the specific weight/style variant to load before re-rendering
    const applyStyle = async (obj, props) => {
      const merged = { fontFamily: obj.fontFamily, fontWeight: obj.fontWeight, fontStyle: obj.fontStyle, fontSize: obj.fontSize, ...props }
      const w = merged.fontWeight === 'bold' ? '700' : '400'
      const s = merged.fontStyle === 'italic' ? 'italic' : 'normal'
      try {
        await document.fonts.load(`${s} ${w} ${merged.fontSize || 24}px '${merged.fontFamily}'`)
      } catch (e) { /* ignore */ }
      obj.set(props)
      obj.dirty = true
      this.canvas.requestRenderAll()
    }
    boldBtn.addEventListener('click', () => {
      const obj = this._getActiveText()
      const isBold = obj ? obj.fontWeight === 'bold' : boldBtn.classList.contains('is-active')
      boldBtn.classList.toggle('is-active', !isBold)
      if (obj) applyStyle(obj, { fontWeight: isBold ? 'normal' : 'bold' })
    })
    italicBtn.addEventListener('click', () => {
      const obj = this._getActiveText()
      const isItalic = obj ? obj.fontStyle === 'italic' : italicBtn.classList.contains('is-active')
      italicBtn.classList.toggle('is-active', !isItalic)
      if (obj) applyStyle(obj, { fontStyle: isItalic ? 'normal' : 'italic' })
    })
    underlineBtn.addEventListener('click', () => {
      const obj = this._getActiveText()
      const isUnder = obj ? !!obj.underline : underlineBtn.classList.contains('is-active')
      underlineBtn.classList.toggle('is-active', !isUnder)
      if (obj) { obj.set('underline', !isUnder); obj.dirty = true; this.canvas.requestRenderAll() }
    })

    // Color
    colorInput.addEventListener('input', () => {
      colorHex.textContent = colorInput.value
      const obj = this._getActiveText()
      if (obj) { obj.set('fill', colorInput.value); obj.dirty = true; this.canvas.requestRenderAll() }
    })

    // Outline toggle + color + width
    const syncOutline = () => {
      outlineSub.hidden = !outlineToggle.checked
      const obj = this._getActiveText()
      if (obj) {
        if (outlineToggle.checked) {
          obj.set({ stroke: outlineColor.value, strokeWidth: parseInt(outlineWidth.value, 10) || 2 })
        } else {
          obj.set({ stroke: null, strokeWidth: 0 })
        }
        obj.dirty = true
        this.canvas.requestRenderAll()
      }
    }
    outlineToggle.addEventListener('change', syncOutline)
    outlineColor.addEventListener('input', syncOutline)
    outlineWidth.addEventListener('input', () => {
      outlineLabel.textContent = outlineWidth.value + 'px'
      syncOutline()
    })

    // Add / Update button
    addBtn.addEventListener('click', () => this._addOrUpdateText())
  }

  _buildFontSelect(select) {
    if (!select) return
    const manifest = Array.isArray(this.config.fontManifest) ? this.config.fontManifest : []
    if (!manifest.length) return

    // Group entries by their `group` field — Monograms first, then Standard, then anything else.
    const groups = new Map()
    for (const f of manifest) {
      if (!f.family) continue
      const g = f.group || 'Other'
      if (!groups.has(g)) groups.set(g, [])
      groups.get(g).push(f)
    }
    const order = ['Monograms', 'Standard', ...[...groups.keys()].filter(g => g !== 'Monograms' && g !== 'Standard')]
    for (const groupName of order) {
      const fonts = groups.get(groupName)
      if (!fonts || !fonts.length) continue
      const og = document.createElement('optgroup')
      og.label = groupName
      for (const f of fonts) {
        const opt = document.createElement('option')
        opt.value = f.family
        opt.textContent = f.family
        opt.style.fontFamily = `'${f.family}'`
        og.appendChild(opt)
      }
      select.appendChild(og)
    }
  }

  /** Backward-compat alias — kept in case other call sites reference this. All
   *  manifest fonts (monograms + standard) are loaded via _loadFonts on first
   *  dialog open. */
  _loadMonogramFont(_family) {
    // no-op — _loadFonts handles all manifest entries up front
    return
  }

  _getActiveText() {
    if (!this.canvas) return null
    const obj = this.canvas.getActiveObject()
    if (obj && (obj.type === 'i-text' || obj.type === 'text' || obj.type === 'textbox')) return obj
    return null
  }

  _syncTextTab(selected) {
    const pane = this.querySelector('[data-tab-pane="text"]')
    if (!pane || !pane.dataset.built) return

    const obj = selected && selected.length === 1
      ? (selected[0].type === 'i-text' || selected[0].type === 'text' || selected[0].type === 'textbox'
          ? selected[0] : null)
      : null

    const textarea     = pane.querySelector('.ec-text-content')
    const fontSelect   = pane.querySelector('#ec-font-select')
    const sizeRange    = pane.querySelector('#ec-font-size')
    const sizeLabel    = pane.querySelector('[data-size-label]')
    const boldBtn      = pane.querySelector('[data-style-bold]')
    const italicBtn    = pane.querySelector('[data-style-italic]')
    const underlineBtn = pane.querySelector('[data-style-underline]')
    const colorInput   = pane.querySelector('#ec-text-color')
    const colorHex     = pane.querySelector('[data-color-hex]')
    const outlineToggle= pane.querySelector('[data-outline-toggle]')
    const outlineSub   = pane.querySelector('[data-outline-sub]')
    const outlineWidth = pane.querySelector('[data-outline-width]')
    const outlineLabel = pane.querySelector('[data-outline-width-label]')
    const addBtn       = pane.querySelector('[data-add-text]')

    if (obj) {
      if (textarea)     textarea.value = obj.text || ''
      if (fontSelect)   fontSelect.value = obj.fontFamily || 'Montserrat'
      const px = Math.round(obj.fontSize || 24)
      if (sizeRange)  sizeRange.value = px
      // Anchor mm labels to the engraving zone width (printable guide), not the
      // whole product photo. Fall back to canvas width if guide width is unset.
      const ppm = (this._engravingPxWidth || this.canvas.width) / this.config.maxWidthMm
      if (sizeLabel)  sizeLabel.textContent = `${px}px · ${(px / ppm).toFixed(1)}mm`
      if (boldBtn)      boldBtn.classList.toggle('is-active',      obj.fontWeight === 'bold')
      if (italicBtn)    italicBtn.classList.toggle('is-active',    obj.fontStyle  === 'italic')
      if (underlineBtn) underlineBtn.classList.toggle('is-active', !!obj.underline)
      if (colorInput && this.config.printable) {
        colorInput.value = obj.fill || '#000000'
        if (colorHex) colorHex.textContent = obj.fill || '#000000'
      }
      const hasStroke = !!(obj.stroke && obj.strokeWidth > 0)
      if (outlineToggle) outlineToggle.checked = hasStroke
      if (outlineSub)    outlineSub.hidden = !hasStroke
      if (outlineWidth && hasStroke) {
        outlineWidth.value = obj.strokeWidth || 2
        if (outlineLabel) outlineLabel.textContent = (obj.strokeWidth || 2) + 'px'
      }
      if (addBtn) addBtn.textContent = 'Update Text'
    } else {
      if (addBtn) addBtn.textContent = 'Add Text'
    }
  }

  _addOrUpdateText() {
    if (!this.canvas) return

    const pane      = this.querySelector('[data-tab-pane="text"]')
    const textarea  = pane && pane.querySelector('.ec-text-content')
    const text      = (textarea ? textarea.value : '').trim()
    if (!text) return

    const fontSelect   = pane.querySelector('#ec-font-select')
    const sizeRange    = pane.querySelector('#ec-font-size')
    const boldBtn      = pane.querySelector('[data-style-bold]')
    const italicBtn    = pane.querySelector('[data-style-italic]')
    const underlineBtn = pane.querySelector('[data-style-underline]')
    const colorInput   = pane.querySelector('#ec-text-color')
    const outlineToggle= pane.querySelector('[data-outline-toggle]')
    const outlineColor = pane.querySelector('[data-outline-color]')
    const outlineWidth = pane.querySelector('[data-outline-width]')

    const fontFamily = fontSelect ? fontSelect.value : 'Montserrat'
    const fontSize   = parseInt(sizeRange ? sizeRange.value : '24', 10)
    const isBold     = boldBtn     && boldBtn.classList.contains('is-active')
    const isItalic   = italicBtn   && italicBtn.classList.contains('is-active')
    const isUnder    = underlineBtn && underlineBtn.classList.contains('is-active')
    const fill       = this.resolveColor(
      (this.config.printable && colorInput) ? colorInput.value : undefined
    )
    const hasOutline = outlineToggle && outlineToggle.checked
    const strokeColor = hasOutline && outlineColor ? outlineColor.value : null
    const strokeWidth = hasOutline && outlineWidth ? parseInt(outlineWidth.value, 10) : 0

    // If a text object is already selected, update it in-place
    const existing = this._getActiveText()
    if (existing) {
      existing.set({
        text: text, fontFamily, fontSize,
        fontWeight:  isBold   ? 'bold'   : 'normal',
        fontStyle:   isItalic ? 'italic' : 'normal',
        underline:   isUnder,
        fill,
        stroke:      hasOutline ? strokeColor : null,
        strokeWidth: strokeWidth,
      })
      existing.dirty = true
      this.canvas.requestRenderAll()
      return
    }

    // Create a new IText
    this._loadMonogramFont(fontFamily)

    const obj = new window.fabric.IText(text, {
      left: this.canvas.width  / 2,
      top:  this.canvas.height / 2,
      originX: 'center',
      originY: 'center',
      fontFamily,
      fontSize,
      fontWeight:  isBold   ? 'bold'   : 'normal',
      fontStyle:   isItalic ? 'italic' : 'normal',
      underline:   isUnder,
      fill,
      stroke:      hasOutline ? strokeColor : null,
      strokeWidth: strokeWidth,
      layerId:   Date.now(),
      layerType: 'text',
    })
    applyObjectDefaults(obj)
    this.canvas.add(obj)
    this.canvas.setActiveObject(obj)
    this.canvas.requestRenderAll()

    const addBtn = pane.querySelector('[data-add-text]')
    if (addBtn) addBtn.textContent = 'Update Text'
  }

  /* ── Object tab ────────────────────────────────────────── */

  _buildObjectUI() {
    const pane = this.querySelector('[data-tab-pane="object"]')
    if (!pane || pane.dataset.built) return
    pane.dataset.built = '1'

    pane.innerHTML = `
      <p class="ec-tab-pane__placeholder" data-object-placeholder>Select an object on the canvas to edit it.</p>

      <div class="ec-obj-controls" data-obj-controls hidden>

        <div class="ec-ctrl-group">
          <label class="ec-label">Align</label>
          <div class="ec-icon-btn-row">
            <button type="button" class="ec-icon-btn" data-align-h title="Center horizontally">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="21" y1="12" x2="3" y2="12"/><path d="M8 7l-5 5 5 5M16 7l5 5-5 5"/></svg>
              <span>Center H</span>
            </button>
            <button type="button" class="ec-icon-btn" data-align-v title="Center vertically">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="21" x2="12" y2="3"/><path d="M7 8l5-5 5 5M7 16l5 5 5-5"/></svg>
              <span>Center V</span>
            </button>
            <button type="button" class="ec-icon-btn" data-flip-x title="Flip horizontal">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3"/><path d="M16 3h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-3"/><line x1="12" y1="3" x2="12" y2="21"/></svg>
              <span>Flip X</span>
            </button>
            <button type="button" class="ec-icon-btn" data-flip-y title="Flip vertical">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v3"/><path d="M21 16v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3"/><line x1="3" y1="12" x2="21" y2="12"/></svg>
              <span>Flip Y</span>
            </button>
          </div>
        </div>

        <div class="ec-ctrl-group" data-distribute-row>
          <label class="ec-label">Distribute</label>
          <div class="ec-icon-btn-row">
            <button type="button" class="ec-icon-btn" data-distribute-h title="Distribute horizontally">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="3" x2="3" y2="21"/><line x1="21" y1="3" x2="21" y2="21"/><rect x="8" y="6" width="8" height="12"/></svg>
              <span>Horiz</span>
            </button>
            <button type="button" class="ec-icon-btn" data-distribute-v title="Distribute vertically">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="3" x2="21" y2="3"/><line x1="3" y1="21" x2="21" y2="21"/><rect x="6" y="8" width="12" height="8"/></svg>
              <span>Vert</span>
            </button>
          </div>
        </div>

        <div class="ec-ctrl-group">
          <label class="ec-label">
            Rotate
            <span class="ec-label-value" data-rotate-label>0°</span>
          </label>
          <input type="range" class="ec-range" data-rotate min="0" max="360" step="1" value="0">
          <div class="ec-snap-btns">
            <button type="button" class="ec-snap-btn" data-snap="0">0°</button>
            <button type="button" class="ec-snap-btn" data-snap="90">90°</button>
            <button type="button" class="ec-snap-btn" data-snap="180">180°</button>
            <button type="button" class="ec-snap-btn" data-snap="270">270°</button>
          </div>
        </div>

        <div class="ec-ctrl-group">
          <label class="ec-label">
            Scale
            <span class="ec-label-value" data-scale-label>1.00×</span>
          </label>
          <input type="range" class="ec-range" data-scale min="0.1" max="5" step="0.05" value="1">
        </div>

        <div class="ec-ctrl-group">
          <label class="ec-label">Layer order</label>
          <div class="ec-icon-btn-row">
            <button type="button" class="ec-icon-btn" data-layer-front title="Bring to front">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 11 12 6 7 11"/><polyline points="17 18 12 13 7 18"/></svg>
              <span>Front</span>
            </button>
            <button type="button" class="ec-icon-btn" data-layer-forward title="Bring forward">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
              <span>Fwd</span>
            </button>
            <button type="button" class="ec-icon-btn" data-layer-backward title="Send backward">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              <span>Back</span>
            </button>
            <button type="button" class="ec-icon-btn" data-layer-back title="Send to back">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="7 13 12 18 17 13"/><polyline points="7 6 12 11 17 6"/></svg>
              <span>Back</span>
            </button>
          </div>
        </div>

        <div class="ec-ctrl-group" data-group-row>
          <button type="button" class="ec-btn ec-btn--ghost ec-btn--sm" data-group-btn></button>
        </div>

        <div class="ec-ctrl-group" data-multi-msg hidden>
          <p class="ec-tab-pane__placeholder" style="padding:8px 0;text-align:left">Multiple objects selected — pick one to edit color or font individually.</p>
        </div>

        <div class="ec-ctrl-group ec-ctrl-group--danger">
          <button type="button" class="ec-btn ec-btn--danger ec-btn--full" data-obj-delete>Delete</button>
        </div>

      </div>
    `

    this._wireObjectControls()
  }

  _wireObjectControls() {
    const pane = this.querySelector('[data-tab-pane="object"]')
    if (!pane) return

    // Alignment
    pane.querySelector('[data-align-h]').addEventListener('click', () => {
      const obj = this.canvas && this.canvas.getActiveObject()
      if (obj) { obj.centerH(); obj.setCoords(); this.canvas.requestRenderAll() }
    })
    pane.querySelector('[data-align-v]').addEventListener('click', () => {
      const obj = this.canvas && this.canvas.getActiveObject()
      if (obj) { obj.centerV(); obj.setCoords(); this.canvas.requestRenderAll() }
    })
    pane.querySelector('[data-flip-x]').addEventListener('click', () => {
      const obj = this.canvas && this.canvas.getActiveObject()
      if (obj) { obj.set('flipX', !obj.flipX); obj.dirty = true; this.canvas.requestRenderAll() }
    })
    pane.querySelector('[data-flip-y]').addEventListener('click', () => {
      const obj = this.canvas && this.canvas.getActiveObject()
      if (obj) { obj.set('flipY', !obj.flipY); obj.dirty = true; this.canvas.requestRenderAll() }
    })

    // Distribute
    pane.querySelector('[data-distribute-h]').addEventListener('click', () => this._distribute('h'))
    pane.querySelector('[data-distribute-v]').addEventListener('click', () => this._distribute('v'))

    // Rotate slider + snap
    const rotateRange = pane.querySelector('[data-rotate]')
    const rotateLabel = pane.querySelector('[data-rotate-label]')
    rotateRange.addEventListener('input', () => {
      const val = parseInt(rotateRange.value, 10)
      rotateLabel.textContent = val + '°'
      const obj = this.canvas && this.canvas.getActiveObject()
      if (obj) { obj.set('angle', val); obj.setCoords(); this.canvas.requestRenderAll() }
    })
    pane.querySelectorAll('[data-snap]').forEach(btn => {
      btn.addEventListener('click', () => {
        const val = parseInt(btn.dataset.snap, 10)
        rotateRange.value = val
        rotateLabel.textContent = val + '°'
        const obj = this.canvas && this.canvas.getActiveObject()
        if (obj) { obj.set('angle', val); obj.setCoords(); this.canvas.requestRenderAll() }
      })
    })

    // Scale slider
    const scaleRange = pane.querySelector('[data-scale]')
    const scaleLabel = pane.querySelector('[data-scale-label]')
    scaleRange.addEventListener('input', () => {
      const val = parseFloat(scaleRange.value)
      scaleLabel.textContent = val.toFixed(2) + '×'
      const obj = this.canvas && this.canvas.getActiveObject()
      if (obj) {
        obj.set({ scaleX: val, scaleY: val })
        obj.setCoords()
        obj.dirty = true
        this.canvas.requestRenderAll()
      }
    })

    // Layer order
    pane.querySelector('[data-layer-front]').addEventListener('click', () => {
      const obj = this.canvas && this.canvas.getActiveObject()
      if (obj) { this.canvas.bringToFront(obj); this.canvas.requestRenderAll(); this._renderLayersStrip() }
    })
    pane.querySelector('[data-layer-forward]').addEventListener('click', () => {
      const obj = this.canvas && this.canvas.getActiveObject()
      if (obj) { this.canvas.bringForward(obj); this.canvas.requestRenderAll(); this._renderLayersStrip() }
    })
    pane.querySelector('[data-layer-backward]').addEventListener('click', () => {
      const obj = this.canvas && this.canvas.getActiveObject()
      if (obj) { this.canvas.sendBackwards(obj); this.canvas.requestRenderAll(); this._renderLayersStrip() }
    })
    pane.querySelector('[data-layer-back]').addEventListener('click', () => {
      const obj = this.canvas && this.canvas.getActiveObject()
      if (obj) { this.canvas.sendToBack(obj); this.canvas.requestRenderAll(); this._renderLayersStrip() }
    })

    // Group / Ungroup
    pane.querySelector('[data-group-btn]').addEventListener('click', () => {
      const obj = this.canvas && this.canvas.getActiveObject()
      if (!obj) return
      if (obj.type === 'group') {
        // Ungroup
        obj.toActiveSelection()
        this.canvas.requestRenderAll()
      } else if (obj.type === 'activeSelection') {
        // Group
        const grp = obj.toGroup()
        grp.set({ layerId: Date.now(), layerType: 'svg' })
        applyObjectDefaults(grp)
        this.canvas.requestRenderAll()
      }
      this._renderLayersStrip()
    })

    // Delete
    pane.querySelector('[data-obj-delete]').addEventListener('click', () => {
      if (!this.canvas) return
      this.canvas.getActiveObjects().forEach(o => this.canvas.remove(o))
      this.canvas.discardActiveObject()
      this.canvas.requestRenderAll()
      this._renderLayersStrip()
    })
  }

  _distribute(axis) {
    if (!this.canvas) return
    const objs = this.canvas.getActiveObjects()
    if (objs.length < 2) return

    if (axis === 'h') {
      const sorted = [...objs].sort((a, b) => a.getBoundingRect().left - b.getBoundingRect().left)
      const first  = sorted[0].getBoundingRect()
      const last   = sorted[sorted.length - 1].getBoundingRect()
      const totalW = sorted.reduce((acc, o) => acc + o.getBoundingRect().width, 0)
      const gap    = (last.left + last.width - first.left - totalW) / (sorted.length - 1)
      let x = first.left
      sorted.forEach(o => {
        const br = o.getBoundingRect()
        const cx = x + br.width / 2
        o.set('left', cx + (o.left - (br.left + br.width / 2)))
        o.setCoords()
        x += br.width + gap
      })
    } else {
      const sorted = [...objs].sort((a, b) => a.getBoundingRect().top - b.getBoundingRect().top)
      const first  = sorted[0].getBoundingRect()
      const last   = sorted[sorted.length - 1].getBoundingRect()
      const totalH = sorted.reduce((acc, o) => acc + o.getBoundingRect().height, 0)
      const gap    = (last.top + last.height - first.top - totalH) / (sorted.length - 1)
      let y = first.top
      sorted.forEach(o => {
        const br = o.getBoundingRect()
        const cy = y + br.height / 2
        o.set('top', cy + (o.top - (br.top + br.height / 2)))
        o.setCoords()
        y += br.height + gap
      })
    }
    this.canvas.requestRenderAll()
  }

  _updateObjectTab(selected) {
    const pane = this.querySelector('[data-tab-pane="object"]')
    if (!pane) return
    if (!pane.dataset.built) this._buildObjectUI()

    const placeholder = pane.querySelector('[data-object-placeholder]')
    const controls    = pane.querySelector('[data-obj-controls]')
    const distributeRow = pane.querySelector('[data-distribute-row]')
    const groupRow    = pane.querySelector('[data-group-row]')
    const multiMsg    = pane.querySelector('[data-multi-msg]')
    const groupBtn    = pane.querySelector('[data-group-btn]')
    const rotateRange = pane.querySelector('[data-rotate]')
    const rotateLabel = pane.querySelector('[data-rotate-label]')
    const scaleRange  = pane.querySelector('[data-scale]')
    const scaleLabel  = pane.querySelector('[data-scale-label]')

    if (!selected || selected.length === 0) {
      if (placeholder) placeholder.hidden = false
      if (controls)    controls.hidden    = true
      return
    }

    if (placeholder) placeholder.hidden = true
    if (controls)    controls.hidden    = false

    const obj = selected.length === 1 ? selected[0] : null
    const multi = selected.length > 1

    // Distribute row — only when multi-select
    if (distributeRow) distributeRow.hidden = !multi
    // Multi-msg
    if (multiMsg) multiMsg.hidden = !multi

    // Group/Ungroup button
    if (groupRow && groupBtn) {
      if (obj && obj.type === 'group') {
        groupRow.hidden = false
        groupBtn.textContent = obj._assetType === 'frame' ? 'Edit Parts' : 'Ungroup'
      } else if (multi) {
        groupRow.hidden = false
        groupBtn.textContent = 'Group'
      } else {
        groupRow.hidden = true
      }
    }

    // Sync rotate + scale sliders to single selection
    if (obj) {
      const angle = Math.round(obj.angle || 0)
      if (rotateRange) rotateRange.value = angle
      if (rotateLabel) rotateLabel.textContent = angle + '°'

      const scale = obj.scaleX || 1
      if (scaleRange) scaleRange.value = scale.toFixed(2)
      if (scaleLabel) scaleLabel.textContent = scale.toFixed(2) + '×'
    }
  }

  /* ── Layers strip ──────────────────────────────────────── */

  _renderLayersStrip() {
    const strip = this.querySelector('[data-layers-strip]')
    if (!strip || !this.canvas) return

    // The engraving-area guide is a visual-only helper — never list it as a layer.
    const objects = this.canvas.getObjects().filter(o => !o._eiGuide)
    strip.innerHTML = ''

    if (objects.length === 0) {
      strip.innerHTML = '<span class="ec-layer-empty">No layers yet</span>'
      return
    }

    objects.forEach((obj) => {
      const card = document.createElement('button')
      card.type = 'button'
      card.className = 'ec-layer-card'
      card.dataset.layerId = obj.layerId || ''

      const iconType = (obj.type === 'i-text' || obj.type === 'text' || obj.type === 'textbox')
        ? 'text'
        : (obj.layerType === 'svg' || obj.type === 'group' ? 'svg' : 'shape')
      const icon = LAYER_ICONS[iconType] || LAYER_ICONS.shape

      let name = 'Object'
      if (obj.type === 'i-text' || obj.type === 'text' || obj.type === 'textbox') {
        name = (obj.text || '').slice(0, 16) || 'Text'
      } else if (obj._assetCategory) {
        name = obj._assetCategory
      } else if (obj._assetId) {
        name = obj._assetId.slice(0, 16)
      } else if (obj.type === 'group') {
        name = 'Group'
      }

      card.innerHTML = `
        <span class="ec-layer-card__icon">${icon}</span>
        <span class="ec-layer-card__name">${name}</span>
        <button type="button" class="ec-layer-card__del" title="Delete" aria-label="Delete layer">×</button>
      `

      // Click card → select on canvas
      card.addEventListener('click', (e) => {
        if (e.target.closest('.ec-layer-card__del')) return
        const target = this.canvas.getObjects().find(o => o.layerId === obj.layerId)
        if (target) {
          this.canvas.setActiveObject(target)
          this.canvas.requestRenderAll()
        }
      })

      // Delete button
      card.querySelector('.ec-layer-card__del').addEventListener('click', (e) => {
        e.stopPropagation()
        this.canvas.remove(obj)
        this.canvas.discardActiveObject()
        this.canvas.requestRenderAll()
        this._renderLayersStrip()
      })

      strip.appendChild(card)
    })

    // Highlight active
    const activeObjs = this.canvas.getActiveObjects()
    if (activeObjs.length > 0) {
      this._highlightLayerCard(activeObjs)
    }
  }

  _highlightLayerCard(selected) {
    const strip = this.querySelector('[data-layers-strip]')
    if (!strip) return
    strip.querySelectorAll('.ec-layer-card').forEach(card => card.classList.remove('is-active'))
    if (!selected) return
    selected.forEach(obj => {
      if (!obj.layerId) return
      const card = strip.querySelector(`[data-layer-id="${obj.layerId}"]`)
      if (card) card.classList.add('is-active')
    })
  }

  _clearLayerHighlight() {
    const strip = this.querySelector('[data-layers-strip]')
    if (!strip) return
    strip.querySelectorAll('.ec-layer-card').forEach(card => card.classList.remove('is-active'))
  }

  /* ── Canvas actions ────────────────────────────────────── */

  clearCanvas() {
    if (!this.canvas) return
    // Keep the engraving-area guide; only clear actual design objects.
    this.canvas.getObjects().slice().forEach(o => { if (!o._eiGuide) this.canvas.remove(o) })
    this.canvas.discardActiveObject()
    this.canvas.requestRenderAll()
    this._renderLayersStrip()
    // Drop any saved-design state on the page so it can't be re-attached on submit
    window.__EI_ENGRAVING__ = null
    if (this._statusEl) {
      this._statusEl.setAttribute('hidden', '')
      if (this._statusText) this._statusText.textContent = ''
    }
  }

  /* ── Keyboard shortcuts ────────────────────────────────── */

  _handleKeydown(e) {
    // Only act when the dialog is open
    if (!this._dialog || !this._dialog.open) return

    // Never fire if focus is inside a text field (UX-patterns §4 / App.vue:167-176)
    if (document.activeElement &&
        document.activeElement.matches('input, textarea, [contenteditable]')) return

    if ((e.key === 'Delete' || e.key === 'Backspace') && this.canvas) {
      e.preventDefault()
      this.canvas.getActiveObjects().forEach(o => this.canvas.remove(o))
      this.canvas.discardActiveObject()
      this.canvas.requestRenderAll()
      this._renderLayersStrip()
      return
    }

    if (e.key === 'Escape' && this.canvas) {
      this.canvas.discardActiveObject()
      this.canvas.requestRenderAll()
      return
    }

    // Ctrl+A — select all
    if ((e.ctrlKey || e.metaKey) && e.key === 'a' && this.canvas) {
      e.preventDefault()
      const objs = this.canvas.getObjects()
      if (objs.length > 0) {
        const sel = new window.fabric.ActiveSelection(objs, { canvas: this.canvas })
        this.canvas.setActiveObject(sel)
        this.canvas.requestRenderAll()
      }
      return
    }

    // Ctrl+D — duplicate selected
    if ((e.ctrlKey || e.metaKey) && e.key === 'd' && this.canvas) {
      e.preventDefault()
      const obj = this.canvas.getActiveObject()
      if (obj) {
        obj.clone(cloned => {
          cloned.set({
            left: obj.left + 10,
            top:  obj.top  + 10,
            layerId: Date.now(),
          })
          this.canvas.add(cloned)
          this.canvas.setActiveObject(cloned)
          this.canvas.requestRenderAll()
          this._renderLayersStrip()
        })
      }
      return
    }

    // Arrow keys — nudge 1px (shift = 10px)
    const nudge = e.shiftKey ? 10 : 1
    const obj = this.canvas && this.canvas.getActiveObject()
    if (obj && ['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(e.key)) {
      e.preventDefault()
      if (e.key === 'ArrowLeft')  obj.set('left', obj.left - nudge)
      if (e.key === 'ArrowRight') obj.set('left', obj.left + nudge)
      if (e.key === 'ArrowUp')    obj.set('top',  obj.top  - nudge)
      if (e.key === 'ArrowDown')  obj.set('top',  obj.top  + nudge)
      obj.setCoords()
      this.canvas.requestRenderAll()
    }
  }

  /* ── Export helpers ────────────────────────────────────── */

  /**
   * Clean per-character IText styles before export.
   * Fabric 5.x bug: styles object accumulates stale per-char entries that break
   * canvas.toJSON() round-trips. Wiping them is safe for engraving (one style per object).
   * Known limitation: mixed per-character font sizes won't survive a re-edit (documented).
   * Ported verbatim from v1 lines ~412-418.
   */
  _cleanStyles() {
    this.canvas.getObjects().forEach(o => {
      if (o.styles && (o.type === 'i-text' || o.type === 'textbox')) {
        o.styles = {}
      }
    })
  }

  /**
   * Detach the engraving-area guide from the canvas for the duration of an export
   * and return its index so it can be re-attached. The guide is a visual-only
   * helper and MUST NOT appear in any exported file (PNG/SVG/JSON/mockup). Even
   * though it carries `excludeFromExport`, `toDataURL()` ignores that flag, so we
   * physically remove it for raster exports — and removing it is also belt-and-
   * suspenders for the vector/JSON paths. Returns -1 if no guide is attached.
   */
  _detachGuide() {
    if (!this._eiGuideObj || !this.canvas) return -1
    const idx = this.canvas.getObjects().indexOf(this._eiGuideObj)
    if (idx >= 0) this.canvas.remove(this._eiGuideObj)
    return idx
  }

  /** Re-attach the guide removed by _detachGuide (idx from that call). */
  _attachGuide(idx) {
    if (idx < 0 || !this._eiGuideObj || !this.canvas) return
    this.canvas.insertAt(this._eiGuideObj, idx, false)
  }

  /**
   * Export canvas as PNG blob — design objects only, transparent background.
   *
   * Two non-obvious fixes baked in:
   *  1. Resets viewportTransform so any zoom/pan can't crop the export.
   *  2. Temporarily flips every object's globalCompositeOperation from
   *     'source-atop' → 'source-over'. We use source-atop on the live canvas
   *     so engraving previews only paint over the product image (looks like
   *     a real engraving). But source-atop renders NOTHING when the canvas
   *     below is transparent — which is exactly the state we want for export.
   *     So we flip the comp op for the duration of the render, then restore.
   */
  _exportPNG() {
    this._cleanStyles()
    const guideIdx = this._detachGuide()   // exclude engraving guide from export
    const bg = this.canvas.backgroundImage
    const prevVT = this.canvas.viewportTransform
    const objs = this.canvas.getObjects()
    const prevCompOps = objs.map(o => o.globalCompositeOperation)

    this.canvas.setBackgroundImage(null, () => {})
    this.canvas.backgroundColor = null
    if (prevVT) this.canvas.setViewportTransform([1, 0, 0, 1, 0, 0])
    objs.forEach(o => { o.globalCompositeOperation = 'source-over' })
    this.canvas.renderAll()

    const url = this.canvas.toDataURL({
      format: 'png',
      multiplier: 4,
      left: 0, top: 0,
      width:  this.canvas.getWidth(),
      height: this.canvas.getHeight(),
    })

    objs.forEach((o, i) => { o.globalCompositeOperation = prevCompOps[i] })
    if (bg) this.canvas.setBackgroundImage(bg, () => {})
    this.canvas.backgroundColor = '#ffffff'
    if (prevVT) this.canvas.setViewportTransform(prevVT)
    this._attachGuide(guideIdx)
    this.canvas.requestRenderAll()
    return this._dataUrlToBlob(url)
  }

  /**
   * Export canvas as SVG blob (raw Fabric toSVG — no background).
   * TODO: replace with exportProductionSVG() once opentype.js text-to-curves export
   *       is implemented (PLAN.md §1.8). That function will:
   *         1. Clone canvas state
   *         2. Convert each IText to paths via opentype.js + font TTF binary
   *         3. Bake all transforms flat, output mm viewBox, no <text>/<g>/transforms
   *         → Corel-compatible, laser-ready SVG
   */
  _exportSVG() {
    this._cleanStyles()
    const guideIdx = this._detachGuide()   // exclude engraving guide from export
    const bg = this.canvas.backgroundImage
    const prevVT = this.canvas.viewportTransform
    const objs = this.canvas.getObjects()
    const prevCompOps = objs.map(o => o.globalCompositeOperation)

    this.canvas.setBackgroundImage(null, () => {})
    if (prevVT) this.canvas.setViewportTransform([1, 0, 0, 1, 0, 0])
    objs.forEach(o => { o.globalCompositeOperation = 'source-over' })

    const svg = this.canvas.toSVG()

    objs.forEach((o, i) => { o.globalCompositeOperation = prevCompOps[i] })
    if (bg) this.canvas.setBackgroundImage(bg, () => {})
    if (prevVT) this.canvas.setViewportTransform(prevVT)
    this._attachGuide(guideIdx)
    this.canvas.requestRenderAll()
    return new Blob([svg], { type: 'image/svg+xml' })
  }

  /**
   * Font-independent "outlined" SVG export — every text object is converted to a
   * `fabric.Path` built from opentype.js glyph outlines, so the file opens with
   * correct shapes on machines/Corel/Illustrator WITHOUT the fonts installed.
   *
   * Produced ALONGSIDE the raw-text `_exportSVG()` (which is kept as `_Design SVG`);
   * this one becomes the new `_Design SVG Curves` property.
   *
   * Behaviour notes:
   *  - Clones the same export prep as `_exportSVG` (clean styles, null bg, identity
   *    viewport, source-over comp op) and FULLY restores afterward.
   *  - OpenType shaping features `liga`/`rlig`/`calt` are enabled so the monogram
   *    fonts' ligature substitutions resolve — this mirrors the browser-shaping the
   *    live editor relies on via `patchFabricForMonograms`.
   *  - Multiline: text is split on `\n`; each line advances the baseline by
   *    `fontSize * lineHeight`, with per-line horizontal offset applied per
   *    `textAlign` using opentype advance widths.
   *  - Per-object try/catch: a glyph-conversion failure leaves the ORIGINAL text
   *    object in place (graceful degradation to raw text for that one object) and
   *    logs a warning. Never throws out of the export.
   *  - The engraving-area guide (`_eiGuide`) is excluded.
   */
  async _exportSVGCurves() {
    this._cleanStyles()

    // Identify the text objects to convert and their z-order, excluding the guide.
    const allObjs = this.canvas.getObjects()
    const isText = (o) => o.type === 'i-text' || o.type === 'text' || o.type === 'textbox'

    // Resolve all needed fonts up front (cached). Map text obj → Font|null.
    const fontByObj = new Map()
    await Promise.all(allObjs.filter(isText).map(async (o) => {
      try { fontByObj.set(o, await this._getOpentypeFont(o)) }
      catch (_) { fontByObj.set(o, null) }
    }))

    // ── Save state we mutate, exactly like _exportSVG ──────────────
    const bg = this.canvas.backgroundImage
    const prevVT = this.canvas.viewportTransform
    const prevCompOps = allObjs.map(o => o.globalCompositeOperation)

    // Track every text→path swap so we can fully reverse it afterward.
    const swaps = []   // { text, path, index }

    for (const obj of allObjs) {
      if (obj._eiGuide) continue
      if (!isText(obj)) continue
      const font = fontByObj.get(obj)
      if (!font) continue   // no usable font → leave raw text (degrade gracefully)

      try {
        const path = this._textObjectToPath(obj, font)
        if (!path) continue
        const index = this.canvas.getObjects().indexOf(obj)
        swaps.push({ text: obj, path, index })
      } catch (e) {
        console.warn('[EngravingCustomizerV2] Curves conversion failed for a text object — leaving raw text:', e && e.message)
      }
    }

    // Apply swaps: remove each text, insert its path at the same z-index.
    for (const s of swaps) {
      this.canvas.remove(s.text)
      this.canvas.insertAt(s.path, s.index, false)
    }

    // Exclude guide + apply export prep (null bg, identity viewport, source-over).
    const guide = this._eiGuideObj && this.canvas.getObjects().includes(this._eiGuideObj) ? this._eiGuideObj : null
    if (guide) this.canvas.remove(guide)

    const exportObjs = this.canvas.getObjects()
    const exportCompOps = exportObjs.map(o => o.globalCompositeOperation)
    this.canvas.setBackgroundImage(null, () => {})
    if (prevVT) this.canvas.setViewportTransform([1, 0, 0, 1, 0, 0])
    exportObjs.forEach(o => { o.globalCompositeOperation = 'source-over' })

    let svg
    try {
      svg = this.canvas.toSVG()
    } finally {
      // ── FULL restore: comp ops, paths→texts, guide, bg, viewport ──
      exportObjs.forEach((o, i) => { o.globalCompositeOperation = exportCompOps[i] })
      // Reverse swaps (lowest index first keeps indices valid).
      swaps.slice().sort((a, b) => a.index - b.index).forEach(s => {
        this.canvas.remove(s.path)
        this.canvas.insertAt(s.text, s.index, false)
      })
      // Restore original comp ops on the (now back) text objects too.
      allObjs.forEach((o, i) => { o.globalCompositeOperation = prevCompOps[i] })
      if (guide) { this.canvas.add(guide); this.canvas.sendToBack(guide) }   // re-add guide at back (visual only)
      if (bg) this.canvas.setBackgroundImage(bg, () => {})
      if (prevVT) this.canvas.setViewportTransform(prevVT)
      this.canvas.requestRenderAll()
    }

    return new Blob([svg], { type: 'image/svg+xml' })
  }

  /**
   * Build a `fabric.Path` from a Fabric text object's glyph outlines using an
   * already-parsed opentype.Font, preserving the SAME visual placement.
   * Returns the Path, or null if no drawable geometry was produced.
   */
  _textObjectToPath(obj, font) {
    const fontSize = obj.fontSize || 24
    const lineHeight = obj.lineHeight || 1.16
    const align = obj.textAlign || 'left'
    const features = { liga: true, rlig: true, calt: true }
    const upm = font.unitsPerEm || 1000

    const lines = String(obj.text == null ? '' : obj.text).split('\n')

    // Per-line advance widths (for alignment) and the widest line as the block width.
    const advances = lines.map(line => {
      try { return font.getAdvanceWidth(line, fontSize, { features, kerning: true }) }
      catch (_) { return 0 }
    })
    const blockWidth = Math.max(0, ...advances)

    // First baseline so the path's top aligns with the text's top.
    const baseline0 = (font.ascender / upm) * fontSize

    let d = ''
    lines.forEach((line, i) => {
      if (!line) return
      let xOffset = 0
      if (align === 'center')      xOffset = (blockWidth - advances[i]) / 2
      else if (align === 'right')  xOffset = (blockWidth - advances[i])
      const y = baseline0 + i * fontSize * lineHeight
      const p = font.getPath(line, xOffset, y, fontSize, { features, kerning: true })
      // getPath already emits screen-space (y-down) coords at the baseline y we
      // passed, so DISABLE toPathData's default flipY (which would invert the
      // glyphs vertically). decimalPlaces:3 keeps fidelity for laser/Corel.
      const seg = p.toPathData({ decimalPlaces: 3, optimize: true, flipY: false })
      if (seg && seg !== 'M0,0Z' && seg.trim()) d += seg
    })

    if (!d) return null

    const path = new window.fabric.Path(d, {
      fill: obj.fill,
      originX: 'left',
      originY: 'top',
      objectCaching: false,
    })

    // Copy the text object's transform so Fabric serializes the path identically.
    path.set({
      left:    obj.left,
      top:     obj.top,
      scaleX:  obj.scaleX,
      scaleY:  obj.scaleY,
      angle:   obj.angle,
      skewX:   obj.skewX,
      skewY:   obj.skewY,
      flipX:   obj.flipX,
      flipY:   obj.flipY,
      originX: obj.originX,
      originY: obj.originY,
    })

    // Placement correction (root-cause of any drift):
    // opentype path coords are emitted with y=0 at the text top-line and x≈0 at the
    // left, but glyph side-bearings / ink extents mean the path's REAL bounding box
    // rarely starts exactly at (0,0). Fabric also derives its own pathOffset from
    // that bbox. So instead of trusting the raw left/top copy, we measure the
    // resulting absolute bounding rect of the path against the original text's
    // bounding rect and nudge left/top by the delta — guaranteeing the curves land
    // exactly where the text was. (The original text obj is still on-canvas here.)
    try {
      path.setCoords()
      const textRect = obj.getBoundingRect(true)   // absolute, ignoring viewport
      const pathRect = path.getBoundingRect(true)
      const dx = textRect.left - pathRect.left
      const dy = textRect.top  - pathRect.top
      if (dx || dy) {
        path.set({ left: path.left + dx, top: path.top + dy })
        path.setCoords()
      }
    } catch (_) { /* best-effort alignment; raw left/top copy stands if this fails */ }

    // Carry layer identity so z-order / layers-strip semantics stay sane.
    if (obj.layerId)   path.layerId = obj.layerId
    if (obj.layerType) path.layerType = obj.layerType

    return path
  }

  /**
   * Export the canvas as a "mockup" PNG that INCLUDES the product image as a
   * background — for the admin / designer to see the design composited on the
   * product. Resets any viewport transform so the export is always a 100%
   * zoomed-out snapshot of the entire canvas, then renders at 4× for crisp
   * thumbnails / print review.
   */
  _exportMockupPNG() {
    this._cleanStyles()
    const guideIdx = this._detachGuide()   // exclude engraving guide from mockup
    const prevVT = this.canvas.viewportTransform
    if (prevVT) this.canvas.setViewportTransform([1, 0, 0, 1, 0, 0])
    const url = this.canvas.toDataURL({
      format: 'png',
      multiplier: 4,
      // Force the whole canvas extent regardless of any zoom/pan
      left: 0, top: 0,
      width:  this.canvas.getWidth(),
      height: this.canvas.getHeight(),
    })
    if (prevVT) this.canvas.setViewportTransform(prevVT)
    this._attachGuide(guideIdx)
    this.canvas.requestRenderAll()
    return this._dataUrlToBlob(url)
  }

  /**
   * Trigger a browser download for an arbitrary blob. Used by the testing
   * Download buttons in the dialog footer.
   */
  _triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  /** Download production SVG (no product image) */
  downloadSVG() {
    const blob = this._exportSVG()
    const ts = new Date().toISOString().replace(/[:.]/g, '-')
    this._triggerDownload(blob, `design-${ts}.svg`)
  }

  /** Download the outlined/curves SVG (text converted to vector paths). */
  async downloadSVGCurves() {
    try {
      const blob = await this._exportSVGCurves()
      const ts = new Date().toISOString().replace(/[:.]/g, '-')
      this._triggerDownload(blob, `design-curves-${ts}.svg`)
    } catch (e) {
      console.error('[EngravingCustomizerV2] Curves SVG download failed:', e)
      alert('Could not generate the outlined SVG. See console for details.')
    }
  }

  /** Download mockup PNG (design composed on product) */
  downloadMockupPNG() {
    const blob = this._exportMockupPNG()
    const ts = new Date().toISOString().replace(/[:.]/g, '-')
    this._triggerDownload(blob, `mockup-${ts}.png`)
  }

  /**
   * Export canvas as JSON blob.
   * Includes custom properties needed for re-edit fidelity.
   * Ported from v1 lines ~442-447.
   */
  _exportJSON() {
    this._cleanStyles()
    const guideIdx = this._detachGuide()   // never persist the engraving guide
    const j = this.canvas.toJSON(JSON_EXTRA_PROPS)
    this._attachGuide(guideIdx)
    delete j.backgroundImage   // background is re-derived from product metafield on load
    return new Blob([JSON.stringify(j)], { type: 'application/json' })
  }

  /**
   * Convert a data-URL to a Blob.
   * Preserved verbatim from v1 lines ~449-456.
   */
  _dataUrlToBlob(url) {
    const [hdr, b64] = url.split(',')
    const mime = hdr.match(/:(.*?);/)[1]
    const raw  = atob(b64)
    const arr  = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i)
    return new Blob([arr], { type: mime })
  }

  /* ── Cart upload dance ─────────────────────────────────── */
  /*
   * Ported from v1 lines 458-499. Semantics preserved exactly.
   * Line item property names (_Design PNG / _Design SVG / _Design JSON / Design)
   * are depended on by snippets/cart.line-item.liquid — do NOT rename.
   */

  /**
   * Upload a design file blob to Shopify CDN via the cart/add.js trick.
   * The upload variant is a hidden $0 variant; Shopify attaches the file to the
   * line item and returns a CDN URL in the properties.
   */
  async _uploadFile(blob, filename) {
    const fd = new FormData()
    fd.append('id',         this.config.uploadVariantId)
    fd.append('quantity',   '1')
    fd.append('properties[_file]', blob, filename)

    const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method:  'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: fd,
    })
    if (!res.ok) throw new Error(`Upload failed: ${res.status}`)
    const data = await res.json()
    const item = data.items ? data.items[0] : data

    // Shopify returns the CDN URL in a property value that contains '/uploads/'
    let fileUrl = null
    if (item.properties) {
      for (const [, val] of Object.entries(item.properties)) {
        if (typeof val === 'string' && val.includes('/uploads/')) {
          fileUrl = val; break
        }
      }
    }
    return fileUrl
  }

  /**
   * Remove all temporary upload-variant line items from the cart.
   * Sequential (Shopify cart returns 409s on concurrent cart/change.js calls).
   * Uses the stable `key` rather than line indices so we don't have to worry
   * about indices shifting between iterations. Errors are swallowed —
   * occasional 400s from cart consolidation are cosmetic.
   */
  async _cleanupUploadItems() {
    try {
      const cartRes = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
      const cart = await cartRes.json()
      const targets = cart.items.filter(it =>
        String(it.variant_id) === String(this.config.uploadVariantId)
      )
      for (const item of targets) {
        try {
          await fetch(`${window.Shopify.routes.root}cart/change.js`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ id: item.key, quantity: 0 }),
          })
        } catch (_) { /* swallow per-item errors */ }
      }
    } catch (e) {
      console.warn('[EngravingCustomizerV2] Cleanup warning (non-blocking):', e.message)
    }
  }

  /* ── Save flow ─────────────────────────────────────────── */

  async saveDesign() {
    // Ignore the engraving-area guide when checking for an empty design.
    const designObjs = this.canvas ? this.canvas.getObjects().filter(o => !o._eiGuide) : []
    if (!this.canvas || designObjs.length === 0) {
      alert('Add something to your design before saving.')
      return
    }

    this._saveBtn.disabled = true
    this._saveBtn.textContent = 'Saving…'
    showBlockingOverlay('Saving your design…')

    try {
      const ts = Date.now()

      // 1. Export files (in-memory, fast)
      const pngBlob    = this._exportPNG()           // transparent design only
      const mockupBlob = this._exportMockupPNG()     // design composed on product
      const svgBlob    = this._exportSVG()           // production SVG (no product)
      const jsonBlob   = this._exportJSON()

      // 2. Upload sequentially.
      //    Shopify's cart endpoint returns 409 Conflict when concurrent
      //    cart/add.js calls hit the same session, so we serialize.
      //    A full-screen blocking overlay (showSavingOverlay) keeps the user
      //    from interacting with the page during the ~3-4s round trips.
      const pngUrl    = await this._uploadFile(pngBlob,    `design-${ts}.png`)
      const mockupUrl = await this._uploadFile(mockupBlob, `mockup-${ts}.png`)
      const svgUrl    = await this._uploadFile(svgBlob,    `design-${ts}.svg`)
      const jsonUrl   = await this._uploadFile(jsonBlob,   `design-${ts}.json`)

      // 2b. Outlined "curves" SVG (text → vector paths via opentype.js).
      //     BEST-EFFORT: a failure here (CDN/parse/CORS) must NEVER block
      //     add-to-cart — the raw `_Design SVG` above already went through.
      let svgCurvesUrl = null
      try {
        const svgCurvesBlob = await this._exportSVGCurves()   // production SVG, text outlined
        svgCurvesUrl = await this._uploadFile(svgCurvesBlob, `design-curves-${ts}.svg`)
      } catch (e) {
        console.warn('[EngravingCustomizerV2] Curves SVG export/upload failed (non-blocking):', e && e.message)
      }

      // 3. Remove all temporary upload items from cart
      await this._cleanupUploadItems()

      // 4. Publish design contract to page
      //    Property names are load-bearing — cart.line-item.liquid and ei-product-gift.liquid
      //    read exactly these keys. DO NOT rename.
      const label = this.config.productTitle || 'Custom engraving'

      window.__EI_ENGRAVING__ = {
        designPng:        pngUrl,       // line item property: _Design PNG
        designMockup:     mockupUrl,    // line item property: _Design Mockup PNG (design on product)
        designSvg:        svgUrl,       // line item property: _Design SVG (production, no product)
        designSvgCurves:  svgCurvesUrl, // line item property: _Design SVG Curves (outlined, font-independent; may be null if export failed)
        designJson:       jsonUrl,      // line item property: _Design JSON
        label,                          // line item property: Design
      }

      // 5. Dispatch event (ei-product-gift.liquid listens for this)
      document.dispatchEvent(new CustomEvent('ei:engraving-saved', {
        detail: window.__EI_ENGRAVING__,
        bubbles: true,
      }))

      // 6. Update status bar
      if (this._statusEl) {
        this._statusEl.removeAttribute('hidden')
        if (this._statusText) this._statusText.textContent = label
      }

      // 7. Close dialog
      this.closeDialog()

    } catch (err) {
      console.error('[EngravingCustomizerV2] Save failed:', err)
      alert('Failed to save design. Please try again.')
    } finally {
      this._saveBtn.disabled = false
      this._saveBtn.textContent = 'Save Design'
      hideBlockingOverlay()
    }
  }
}

/* ──────────────────────────────────────────────────────────── */
/*  Full-screen blocking overlay                                */
/*    Used during multi-step Shopify round trips (saveDesign +  */
/*    add-to-cart) so the user can't double-click or navigate   */
/*    away mid-flight. Singleton; multiple show calls are       */
/*    counted so concurrent operations all clear before we hide.*/
/* ──────────────────────────────────────────────────────────── */
let _blockingOverlayCount = 0
let _blockingOverlayEl = null

function _ensureBlockingOverlayEl() {
  if (_blockingOverlayEl) return _blockingOverlayEl
  if (!document.getElementById('ei-blocking-overlay-style')) {
    const style = document.createElement('style')
    style.id = 'ei-blocking-overlay-style'
    style.textContent = `
      .ei-blocking-overlay {
        position: fixed; inset: 0;
        background: rgba(20, 20, 20, 0.78);
        z-index: 2147483647;
        display: flex; flex-direction: column;
        align-items: center; justify-content: center;
        gap: 18px;
        backdrop-filter: blur(2px);
        -webkit-backdrop-filter: blur(2px);
        animation: ei-overlay-fade 0.18s ease-out;
        pointer-events: auto;
      }
      .ei-blocking-overlay__spinner {
        width: 68px; height: 68px;
        border: 6px solid rgba(255, 255, 255, 0.18);
        border-top-color: #ffffff;
        border-radius: 50%;
        animation: ei-overlay-spin 0.85s linear infinite;
      }
      .ei-blocking-overlay__text {
        color: #ffffff;
        font-family: -apple-system, BlinkMacSystemFont, 'Plus Jakarta Sans', system-ui, sans-serif;
        font-size: 1.0625rem;
        font-weight: 600;
        letter-spacing: 0.01em;
        text-align: center;
        max-width: 80%;
      }
      .ei-blocking-overlay__sub {
        color: rgba(255, 255, 255, 0.72);
        font-size: 0.8125rem;
        font-weight: 400;
        margin-top: -10px;
      }
      @keyframes ei-overlay-spin { to { transform: rotate(360deg); } }
      @keyframes ei-overlay-fade { from { opacity: 0; } to { opacity: 1; } }
    `
    document.head.appendChild(style)
  }
  const el = document.createElement('div')
  el.className = 'ei-blocking-overlay'
  el.setAttribute('role', 'status')
  el.setAttribute('aria-live', 'polite')
  el.setAttribute('aria-busy', 'true')
  el.innerHTML = `
    <div class="ei-blocking-overlay__spinner" aria-hidden="true"></div>
    <div class="ei-blocking-overlay__text" data-text>Working…</div>
    <div class="ei-blocking-overlay__sub">Please don't close this window.</div>
  `
  // Eat all events behind the overlay
  ;['click', 'keydown', 'wheel', 'touchstart'].forEach(evt =>
    el.addEventListener(evt, (e) => { e.stopPropagation() }, { capture: true })
  )
  _blockingOverlayEl = el
  return el
}

function showBlockingOverlay(text) {
  const el = _ensureBlockingOverlayEl()
  el.querySelector('[data-text]').textContent = text || 'Working…'

  // Native <dialog> opened via showModal() lives in the browser's top layer,
  // which is above any z-index in the normal document flow. To stack ABOVE
  // an open modal we must mount the overlay INSIDE that dialog (it inherits
  // the top layer). If no modal is open, body is fine.
  const openDialog = document.querySelector('dialog[open]')
  const targetParent = openDialog || document.body
  if (el.parentNode !== targetParent) {
    if (el.parentNode) el.parentNode.removeChild(el)
    targetParent.appendChild(el)
  }
  document.body.style.overflow = 'hidden'
  _blockingOverlayCount++
}

function hideBlockingOverlay() {
  _blockingOverlayCount = Math.max(0, _blockingOverlayCount - 1)
  if (_blockingOverlayCount === 0 && _blockingOverlayEl && _blockingOverlayEl.parentNode) {
    _blockingOverlayEl.parentNode.removeChild(_blockingOverlayEl)
    document.body.style.overflow = ''
  }
}

// Expose on window so the section's submit handler can use the same overlay
window.__EI_BLOCKING_OVERLAY__ = { show: showBlockingOverlay, hide: hideBlockingOverlay }

/* ── Register ──────────────────────────────────────────────── */

if (!customElements.get('engraving-customizer-v2')) {
  customElements.define('engraving-customizer-v2', EngravingCustomizerV2)
}
